/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TChannel, TPreview } from "@tivocorp/data-domain-lib";

import type { Session } from "../../model/entities";
import { ActionKernelType } from "../../model/types";
import {
  ErrorPriority,
  PlayerState,
  PlaybackContentType,
  MediaPaneType,
  MetadataState,
  MetadataType,
  PlaybackRestrictionType,
  SessionState
} from "../../model/videoplayer/enums";
import {
  PlaybackError,
  PlaybackSetupErrorCode,
  RestrictionErrorCode
} from "../../model/videoplayer/error";
import type {
  IPlayable,
  ITrickPlayEligibility
} from "../../model/videoplayer/interfaces";
import type { TSeekRange } from "../../model/videoplayer/types";
import { VideoPlaybackController } from "../../model/videoplayer/VideoPlaybackController";
import type { BeforeNavigateHandler } from "../../model/videoplayer/VideoPlaybackController";
import type { TrickPlayMode } from "../../model/videoplayer/trickplay";
import {
  Direction,
  DEFAULT_ELIGIBILITY,
  DEFAULT_TRICKPLAY_CONFIG,
  SpeedLevel
} from "../../model/videoplayer/trickplay";
import type { TrickPlayManager } from "../../model/videoplayer/trickplay";
import type { ISubscription } from "../../model/videoplayer/ISubscription";
import type { TStandalonePlaybackEvent } from "../../model/videoplayer/types";
import { AiringPreviewUtils } from "../../model/videoplayer/utils/AiringPreviewUtils";
import { ThumbnailManager } from "../../model/videoplayer/impls";
import { PlaybackURLManager } from "../../model/videoplayer/managers";
import { DeviceModel } from "../../model/device/DeviceModel";
import { getDeviceMemoryTier } from "../../model/device/DeviceMemoryTier";
import type { TDeviceMemoryTier } from "../../model/device/DeviceMemoryTier";
import { LOW_MEMORY_10FOOT_OVERRIDES } from "../../model/videoplayer/impls/ThumbnailManager10FootConfig";
import { formatAiringClockTime } from "../../model/utility/formatAiringClockTime";
import { formatElapsedTime } from "../../model/utility/formatElapsedTime";
import { Log } from "../../model/logging";
import {
  ChannelsInteractor,
  RecordingsServiceInteractor
} from "../../model/interactor";
import type { UnityAppDispatch, UnityRootState } from "../UnityAppStore";
import { selectIsNpvrRecordingAllowed } from "../guide";
import {
  selectHasOnePassCreateActionKernel,
  selectHasOnePassModifyActionKernel,
  selectWasPiGEnteredFromMiniClick,
  selectAiringEndEpoch,
  selectOnePassActionInfo,
  selectOnePassModifyInfo
} from "./videoPlayerUi.selectors";
import {
  playbackStateUpdated,
  airingInfoUpdated,
  trickPlayStateUpdated,
  trickPlayEligibilityUpdated,
  speedTriggerMethodUpdated,
  playableUpdated,
  skipOccurred,
  audioTracksUpdated,
  mediaTracksReadyUpdated,
  actionKernelTypesUpdated,
  metadataStateUpdated,
  sessionStateUpdated,
  exitToPreviousScreenRequested,
  standalonePlaybackStateUpdated,
  videoPlayerUiReset
} from "./videoPlayerUiSlice";
import { Locale, LocaleKeys } from "../../model/locale";
import { LocationUrls } from "../../model/navigation/LocationUrls";
import { showToastMessage } from "../toastMessage";
import { dismissError, displayError } from "../errorDisplay";
import { selectIsOsdVisible, selectOsdPriority } from "../osd";
import {
  selectIsUnityDialogVisible,
  selectUnityDialogErrorCode,
  UnityDialogActionConfig
} from "../unityDialog";
import { GenericErrorCode } from "../../model/videoplayer/error";
import { Branding } from "../../model/branding";
import { BrandingUiInfoKeys } from "../../model/entities";
import { fetchMyHubRecordingQuota } from "../myHub";
import type { TMyHubRecordingQuota } from "../myHub";
import { loadSeriesRecordingOptions } from "../myHub/seriesRecordingOptions";
import { dismissUnityDialog, showUnityDialog } from "../unityDialog";
import type { TOnePassActionInfo } from "./videoPlayerUi.types";
import { selectPreviousUrl } from "../screens";
import { recordingActionErrorMapper } from "../../model/errorMapper/ScheduleActionErrorMappers";

const TAG: string = "videoPlayerUiThunks";

function getRestrictionToastMessage(direction: Direction): string {
  if (direction === Direction.Forward) {
    return Locale.getString(LocaleKeys.PLAYBACK_NOT_FAST_FORWARDABLE_MESSAGE);
  }

  return Locale.getString(LocaleKeys.PLAYBACK_NOT_REWINDABLE_MESSAGE);
}

// ── Module-level state (non-serializable — kept outside Redux) ──

let stateChangeUnsubscribe: (() => void) | null = null;
let playableChangeUnsubscribe: (() => void) | null = null;
let metadataStateUnsubscribe: (() => void) | null = null;
let playbackErrorUnsubscribe: (() => void) | null = null;
let exitToPreviousScreenUnsubscribe: (() => void) | null = null;
let playbackRestrictionUnsubscribe: (() => void) | null = null;
let sessionStateChangeUnsubscribe: (() => void) | null = null;
let standalonePlaybackUnsubscribe: (() => void) | null = null;

/** Last known player state — updated by listener, read by refresh. */
let lastKnownPlayerState: PlayerState = PlayerState.IDLE;

/**
 * Whether the current load has produced its first frame, at which point the
 * playback position has settled (for Start Over this is where the resume seek
 * is applied). Latched true on FIRST_FRAME, reset on a new load (LOADING) and
 * on cleanup. Used to withhold the seek-bar knob, current-time label, and
 * thumbnail until the position settles so they do not flash at a transient
 * position — applies to all playback types.
 */
let hasFirstFramePlayed: boolean = false;

/**
 * Combined audio + CC/subtitle track count as of the last dispatch of
 * mediaTracksReadyUpdated. Lets the listener only re-dispatch when the
 * count actually changes instead of on every player state change.
 * Reset on cleanup so a fresh subscribe re-evaluates from scratch.
 */
let lastMediaTracksLength: number = 0;

/** Cached airing references for change-detection. */
let prevAirings: Record<string, unknown>[] | undefined = undefined;
let prevPreview: Record<string, unknown> | undefined = undefined;
let airingUtils: AiringPreviewUtils | null = null;

const isActionKernelType = (value: unknown): value is ActionKernelType => {
  return typeof value === "string";
};

/** Reads `key` off an unknown object, or undefined when it is not there. */
function readProperty(source: unknown, key: string): unknown {
  if (typeof source !== "object" || source === null || !(key in source)) {
    return undefined;
  }

  return (source as Record<string, unknown>)[key];
}

const getActionKernelType = (action: unknown): ActionKernelType | null => {
  if (typeof action !== "object" || action === null || !("kernel" in action)) {
    return null;
  }

  const kernel: unknown = action.kernel;
  if (
    typeof kernel !== "object" ||
    kernel === null ||
    !("actionKernelType" in kernel)
  ) {
    return null;
  }

  const actionKernelType: unknown = kernel.actionKernelType;
  if (!isActionKernelType(actionKernelType)) {
    return null;
  }

  return actionKernelType as ActionKernelType;
};

function getActionKernelTypesFromMetadata(
  vpc: VideoPlaybackController
): ReadonlyArray<ActionKernelType> {
  const metadata = vpc.getMetadata();
  if (metadata.actions === undefined || metadata.actions === null) {
    return [];
  }

  return metadata.actions.reduce(
    (acc: Array<ActionKernelType>, action): Array<ActionKernelType> => {
      const actionKernelType: ActionKernelType | null =
        getActionKernelType(action);
      if (actionKernelType !== null) {
        acc.push(actionKernelType);
      }
      return acc;
    },
    []
  );
}

const ONE_PASS_CREATE_KERNEL_TYPES: ReadonlySet<string> = new Set([
  ActionKernelType.CreateOnePassActionKernelV2,
  ActionKernelType.CreateMultiOnePassActionKernel
]);

/**
 * Attempts to extract OnePass action info from a single action object.
 *
 * @param action - The action to validate and extract from.
 * @returns OnePass action info if valid, null otherwise.
 */
function tryExtractOnePassInfo(action: unknown): TOnePassActionInfo | null {
  if (typeof action !== "object" || action === null) {
    return null;
  }

  const kernel: unknown = "kernel" in action ? action.kernel : null;
  if (typeof kernel !== "object" || kernel === null) {
    return null;
  }

  const kernelType: unknown =
    "actionKernelType" in kernel ? kernel.actionKernelType : null;
  if (
    typeof kernelType !== "string" ||
    !ONE_PASS_CREATE_KERNEL_TYPES.has(kernelType)
  ) {
    return null;
  }

  const ucid: unknown =
    "unifiedCollectionId" in kernel ? kernel.unifiedCollectionId : null;
  const title: unknown = "title" in action ? action.title : null;

  if (typeof ucid !== "string" || typeof title !== "string") {
    return null;
  }

  return { ucid, title };
}

/**
 * Where each modify-OnePass kernel keeps its collection id. The multi variant
 * carries a nested action list instead, so it has none to offer.
 */
const ONE_PASS_MODIFY_COLLECTION_ID_FIELDS: Readonly<Record<string, string>> = {
  [ActionKernelType.ModifyOnePassActionKernelV2]: "unifiedCollectionId",
  [ActionKernelType.ModifyOnePassActionKernel]: "collectionId"
};

/**
 * Reads the series collection id and title from the modify-OnePass action,
 * present once the series already has a OnePass. Null when no such action
 * carries a collection id.
 */
function getOnePassModifyInfoFromMetadata(
  vpc: VideoPlaybackController
): TOnePassActionInfo | null {
  const metadata = vpc.getMetadata();
  if (metadata.actions === undefined || metadata.actions === null) {
    return null;
  }

  for (const action of metadata.actions) {
    const kernel: unknown = readProperty(action, "kernel");
    const kernelType: unknown = readProperty(kernel, "actionKernelType");
    const field: string | undefined =
      typeof kernelType === "string"
        ? ONE_PASS_MODIFY_COLLECTION_ID_FIELDS[kernelType]
        : undefined;
    if (field === undefined) {
      continue;
    }
    const ucid: unknown = readProperty(kernel, field);
    if (typeof ucid !== "string" || ucid === "") {
      continue;
    }
    const title: unknown = readProperty(action, "title");
    return { ucid, title: typeof title === "string" ? title : "" };
  }

  return null;
}

function getOnePassActionInfoFromMetadata(
  vpc: VideoPlaybackController
): TOnePassActionInfo | null {
  const metadata = vpc.getMetadata();
  if (metadata.actions === undefined || metadata.actions === null) {
    return null;
  }

  for (const action of metadata.actions) {
    const info = tryExtractOnePassInfo(action);
    if (info !== null) {
      return info;
    }
  }

  return null;
}

/**
 * Reads the collection id the manage-recordings screen is keyed by.
 *
 * Preferred source is the modify-recording action, which carries it on its
 * nested cancel action:
 *
 *   modifyRecordingActionKernel → cancelAction → kernel → collectionId
 *
 * That is the collection of the recording being modified, rather than merely of
 * the content. The modify kernel itself holds only offerId/contentId, so there
 * is nowhere else in that action to look — and `cancelAction` is optional, so
 * this falls back to the offer preview's collectionId, which the preview
 * service flattens up from `contentPreview` and which survives an action-list
 * refresh. For an episode that is the series collection, i.e. the same thing
 * the screen wants.
 *
 * Null when neither is present; the caller then opens My Hub instead.
 */
function getModifyRecordingCollectionIdFromMetadata(
  vpc: VideoPlaybackController
): string | null {
  const metadata = vpc.getMetadata();
  if (metadata.actions === undefined || metadata.actions === null) {
    return null;
  }

  for (const action of metadata.actions) {
    const kernel: unknown = readProperty(action, "kernel");
    if (
      readProperty(kernel, "actionKernelType") !==
      ActionKernelType.ModifyRecordingActionKernel
    ) {
      continue;
    }

    const cancelKernel: unknown = readProperty(
      readProperty(kernel, "cancelAction"),
      "kernel"
    );
    const collectionId: unknown = readProperty(cancelKernel, "collectionId");
    if (typeof collectionId === "string" && collectionId !== "") {
      return collectionId;
    }
  }

  const previewCollectionId: unknown = readProperty(
    metadata.offerPreview,
    "collectionId"
  );
  if (typeof previewCollectionId === "string" && previewCollectionId !== "") {
    return previewCollectionId;
  }

  return null;
}

export const refreshActionKernelTypes =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const actionKernelTypes: ReadonlyArray<ActionKernelType> =
      getActionKernelTypesFromMetadata(vpc);
    const onePassActionInfo: TOnePassActionInfo | null =
      getOnePassActionInfoFromMetadata(vpc);

    dispatch(
      actionKernelTypesUpdated({
        actionKernelTypes: actionKernelTypes,
        onePassActionInfo: onePassActionInfo,
        onePassModifyInfo: getOnePassModifyInfoFromMetadata(vpc),
        modifyRecordingCollectionId:
          getModifyRecordingCollectionIdFromMetadata(vpc)
      })
    );
  };

// ── Thumbnail manager ──

/** Returns whether native browser PiP is currently active. */
export function isBrowserPiPActive(): boolean {
  return VideoPlaybackController.getInstance().isBrowserPiPActive();
}

/** Sets whether native browser PiP is currently active. */
export function setBrowserPiPActive(value: boolean): void {
  VideoPlaybackController.getInstance().setBrowserPiPActive(value);
}

/** Returns whether the player was playing when PiP was entered. */
export function wasPlayingOnPiPEntry(): boolean {
  return VideoPlaybackController.getInstance().wasPlayingOnPiPEntry();
}

/** Sets whether the player was playing when PiP was entered. */
export function setPlayingOnPiPEntry(value: boolean): void {
  VideoPlaybackController.getInstance().setPlayingOnPiPEntry(value);
}

/** Returns whether the next FullScreenPlayback mount is a PiP-return path. */
export function isPiPReturnPending(): boolean {
  return VideoPlaybackController.getInstance().isPiPReturnPending();
}

/** Sets whether the next FullScreenPlayback mount is a PiP-return path. */
export function setPiPReturnPending(value: boolean): void {
  VideoPlaybackController.getInstance().setPiPReturnPending(value);
}

/** Returns whether a PiP entry request is already in flight. */
export function isPipEntryPending(): boolean {
  return VideoPlaybackController.getInstance().isPipEntryPending();
}

/** Sets whether a PiP entry request is already in flight. */
export function setPipEntryPending(value: boolean): void {
  VideoPlaybackController.getInstance().setPipEntryPending(value);
}

function buildThumbnailUrlTemplate(
  channel: TChannel | undefined,
  startEpoch: number,
  endEpoch: number
): string {
  const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
  const eligibility: ITrickPlayEligibility | null =
    vpc.getTrickPlayEligibility();
  // The v2 session's seekUrl / socuSeekUrl, once it has answered, take over
  // from the pre-session template. A changed template moves the source key in
  // tryLoadThumbnails(), which is what reloads the frames.
  const activeSession: Session | null = vpc.getActiveSession();
  const session: Session | undefined =
    activeSession !== null ? activeSession : undefined;

  if (vpc.isVod()) {
    return playbackURLManager.getVodThumbnailUrlTemplate(
      vpc.getStreamingUrl(),
      session
    );
  }

  if (vpc.isNdvr()) {
    return playbackURLManager.getNdvrThumbnailUrlTemplate(
      vpc.getStreamingUrl(),
      startEpoch,
      endEpoch,
      session
    );
  }

  // LIVE template is used only for live playback when StartOver is NOT available
  // AND trickplay is allowed (live-only, timeshift mode).
  // SOCU playback (StartOver / CatchUp) always reads from the recording host:
  // its frames only exist there, and getStartOverAvailable() (derived from the
  // preview's isAvailableInSocu) says nothing about an already-recorded
  // CatchUp program, so it must not select the live host here.
  const useLiveThumbnailTemplate: boolean =
    !vpc.isSocu() &&
    !vpc.getStartOverAvailable() &&
    eligibility !== null &&
    eligibility.trickPlayAllowed;

  // Both templates come off the channel's own tuningUri — `thumbnail-live://`
  // or `thumbnail-socu://`. There is no client-side fallback host, so a
  // channel that delivers neither yields "" and the caller skips the load.
  if (useLiveThumbnailTemplate) {
    return playbackURLManager.getLiveThumbnailUrlTemplate(channel, session);
  }

  return playbackURLManager.getSocuThumbnailUrlTemplate(channel, session);
}

/**
 * No thumbnails to fetch: with trick-play disallowed there is nothing
 * seekable to preview. Covers TiVo+ too — its eligibility is always
 * derived with trickPlayAllowed=false — without checking the channel itself.
 */
function isThumbnailFetchBlocked(
  eligibility: ITrickPlayEligibility | null
): boolean {
  return eligibility !== null && !eligibility.trickPlayAllowed;
}

/**
 * Identifies the content a set of thumbnails belongs to: the channel for
 * linear/SOCU, the asset for VOD/nDVR, plus the playback content type.
 *
 * Deliberately excludes offerId — VPC re-notifies the same content with the
 * offerId filled in on a Start Over switch, and that is not new content.
 */
function getThumbnailPlayableIdentity(playable: IPlayable | null): string {
  if (playable === null) {
    return "";
  }
  const channelId: string =
    playable.channel !== undefined && playable.channel !== null
      ? String(playable.channel.channelId)
      : "";
  let assetId: string = "";
  if (playable.vodPlaybackModel !== undefined) {
    assetId = playable.vodPlaybackModel.id;
  } else if (playable.ndvrPlaybackModel !== undefined) {
    assetId = playable.ndvrPlaybackModel.id;
  }
  return playable.playbackContentType + "|" + channelId + "|" + assetId;
}

/**
 * Drops every loaded thumbnail and in-flight fetch so frames from the content
 * just left can neither be served nor keep being downloaded. No-op when
 * nothing is loaded.
 */
function releaseThumbnails(reason: string): void {
  if (!thumbnailsLoaded) {
    return;
  }
  Log.d(TAG, "releaseThumbnails() — " + reason);
  thumbnailManager.resetThumbnails();
  thumbnailsLoaded = false;
  thumbnailSourceKey = null;
  thumbnailPlayableIdentity = null;
}

/**
 * Whether the airing metadata VPC currently holds describes the playing
 * channel.
 *
 * VPC switches the playable before the new channel's metadata arrives, so for
 * a moment after a channel change the airing still belongs to the previous
 * channel. Loading against it would key thumbnails on the wrong airing window
 * and force a second reset when the real metadata lands — and that second
 * reset clears the LOADED/FIRST_FRAME latches after they have already fired,
 * so the new channel would never get its initial preload. Unknown station ids
 * on either side are treated as matching.
 */
function isAiringForPlayable(
  airingOffers: Array<Record<string, unknown>> | undefined,
  playable: IPlayable | null
): boolean {
  if (
    airingOffers === undefined ||
    airingOffers === null ||
    airingOffers.length === 0 ||
    playable === null ||
    playable.channel === undefined ||
    playable.channel === null
  ) {
    return true;
  }
  const airingStationId: unknown = airingOffers[0].stationId;
  const channelStationId: unknown = playable.channel.stationId;
  if (
    airingStationId === undefined ||
    airingStationId === null ||
    channelStationId === undefined ||
    channelStationId === null
  ) {
    return true;
  }
  return String(airingStationId) === String(channelStationId);
}

/**
 * Attempts to load thumbnails once when airing epochs and channel info
 * are available.  Extracted to keep refreshPlaybackState complexity low.
 */
function tryLoadThumbnails(
  airingStartEpoch: number,
  airingEndEpoch: number
): void {
  const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
  // The player timeline starts at the recorded window, which a partial recording narrows.
  const { startEpoch, endEpoch } = vpc.isNdvr()
    ? playbackURLManager.getNdvrRecordingWindow(vpc.getStreamingUrl())
    : { startEpoch: airingStartEpoch, endEpoch: airingEndEpoch };
  // VOD's timeline is content-relative, so its start is legitimately 0.
  if (endEpoch <= 0 || (startEpoch <= 0 && !vpc.isVod())) {
    return;
  }

  // Each early exit below means the current content has no thumbnails, so
  // whatever is still loaded belongs to something else (typically the
  // previous channel) and must be released rather than left to be served on
  // FF/RW — the cache is keyed by wall-clock epoch, which every live channel
  // shares.
  if (isThumbnailFetchBlocked(vpc.getTrickPlayEligibility())) {
    releaseThumbnails("trick-play disallowed for current content");
    return;
  }

  const playableForTemplate = vpc.getPlayable();
  const urlTemplate: string = buildThumbnailUrlTemplate(
    playableForTemplate !== null ? playableForTemplate.channel : undefined,
    startEpoch,
    endEpoch
  );

  // The channel's tuning URIs are the only source of a thumbnail host, so a
  // channel that delivers no thumbnail entry has no frames this client can
  // address. Skipped rather than guessed at: trick play keeps working, just
  // without the filmstrip.
  if (urlTemplate === "") {
    Log.w(
      TAG,
      "tryLoadThumbnails() — channel delivers no thumbnail template; skipping"
    );
    releaseThumbnails("current content has no thumbnail template");
    return;
  }

  // Keyed on the airing window as well as the template, not the template
  // alone: the SOCU template no longer embeds the start/end epochs, so two
  // different airings on one channel resolve to the identical URL and a
  // template-only key would skip the reload the new airing needs.
  const sourceKey: string =
    urlTemplate + "|" + String(startEpoch) + "-" + String(endEpoch);

  if (thumbnailsLoaded && thumbnailSourceKey === sourceKey) {
    return;
  }

  releaseThumbnails("thumbnail source changed");

  Log.d(
    TAG,
    "Loading thumbnails template=" +
      urlTemplate +
      " start=" +
      String(startEpoch) +
      " end=" +
      String(endEpoch)
  );
  const playable = vpc.getPlayable();
  const contentType =
    playable !== null ? playable.playbackContentType : undefined;
  // Derive isLive from content type so the live-edge timer starts correctly
  // on 10-foot devices (LIVE maps to PlaybackCategory "LIVE" which gates
  // schedule10FootLiveFetch). Previously this was hardcoded to false.
  const isLiveContent = contentType === PlaybackContentType.LIVE;
  thumbnailManager.loadThumbnails(
    startEpoch,
    endEpoch,
    urlTemplate,
    isLiveContent,
    contentType
  );
  thumbnailsLoaded = true;
  thumbnailSourceKey = sourceKey;
  thumbnailPlayableIdentity = getThumbnailPlayableIdentity(playable);
}

/**
 * Live-path wrapper around tryLoadThumbnails(): held back while the airing
 * still belongs to the previous channel — see isAiringForPlayable().
 */
function tryLoadLiveThumbnails(
  airingOffers: Array<Record<string, unknown>> | undefined,
  startEpoch: number,
  endEpoch: number
): void {
  const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
  if (!isAiringForPlayable(airingOffers, vpc.getPlayable())) {
    return;
  }
  tryLoadThumbnails(startEpoch, endEpoch);
}

/**
 * The single ThumbnailManager for the app's lifetime.
 *
 * Deliberately never reassigned: replacing the instance orphaned any fetch
 * continuation still holding a reference to the old one, and because nothing
 * could reach that instance afterwards, the blob URLs it went on to create
 * could never be revoked. resetThumbnails() clears all per-content state while
 * preserving the 10-foot strategy flag and config, so reuse is the intended
 * contract.
 */
const thumbnailManager: ThumbnailManager = new ThumbnailManager();
let thumbnailsLoaded: boolean = false;
let thumbnailSourceKey: string | null = null;
/** getThumbnailPlayableIdentity() of the content the thumbnails were loaded for. */
let thumbnailPlayableIdentity: string | null = null;

/** Single source of the SOCU/recording host for thumbnail URL templates. */
const playbackURLManager: PlaybackURLManager = new PlaybackURLManager();

function resolveSocuThumbnailEpochRange(
  currentAiringUtils: AiringPreviewUtils,
  seekRange: TSeekRange,
  endTimeDuration: number
): { startEpoch: number; endEpoch: number } {
  const socuStartEpoch: number = currentAiringUtils.getStartTimeEpoch();
  const socuEndEpoch: number = currentAiringUtils.getEndTimeEpoch();
  return {
    startEpoch: socuStartEpoch > 0 ? socuStartEpoch : seekRange.start,
    endEpoch:
      socuEndEpoch > 0 ? socuEndEpoch : seekRange.start + endTimeDuration
  };
}

/**
 * Synchronous helper for the View layer to retrieve the cached thumbnail
 * blob URL for a given absolute epoch position.
 *
 * @param seekEpoch - Absolute epoch seconds of the seek target.
 * @param airingStartEpoch - Airing start epoch (to compute relative offset).
 * @returns Blob URL string or null.
 */
/**
 * The thumbnail URL template the filmstrip is currently loaded with, or
 * `""` when the current content resolved none.
 *
 * Exposed for the Video Debug Overlay, which shows it as "Thumbnail URL".
 * The template is an application concern rather than a player one — it is
 * resolved here from the channel's tuning URIs, not by Shaka — so the
 * overlay's player-facing metrics provider cannot see it and reads it
 * through this accessor instead.
 *
 * @returns The active template with its `{FILENAME}` placeholder intact.
 */
export function getThumbnailUrlTemplate(): string {
  return thumbnailManager.getUrlTemplate();
}

export function getThumbnailForPosition(
  seekEpoch: number,
  airingStartEpoch: number
): string | null {
  if (!thumbnailsLoaded) {
    return null;
  }
  // On 10-foot devices, seekEpoch is the RAW player position (the D-pad
  // trick-play target / current player time).  Route it through the manager's
  // player-position lookup so it is normalised the same way as the prefetch
  // path (to10FootDomain) — independent of airingStartEpoch, whose base is
  // inconsistent across categories (0 for StartOver vs the airing epoch for
  // CatchUp/NDVR/VOD).  Using the relative position here would make every
  // CatchUp/NDVR/VOD lookup miss and fall back to single on-demand fetches.
  if (DeviceModel.isTvDevice()) {
    return thumbnailManager.getThumbnailForPlayerPosition(seekEpoch);
  }
  const relativePosition: number = seekEpoch - airingStartEpoch;
  return thumbnailManager.getThumbnail(relativePosition);
}

/**
 * Subscribe to thumbnail arrivals so a View can re-run getThumbnailForPosition.
 *
 * getThumbnailForPosition() is synchronous but the fetch behind it is not: on a
 * miss it returns the last genuinely-loaded frame and queues the real one. A
 * caller that resolves the URL once per seek position — which is the whole
 * point of not doing the lookup on every render — must re-resolve when the
 * fetch lands, or it shows the previous frame until the position moves on.
 *
 * @param listener - Invoked after each successful thumbnail fetch.
 * @returns An unsubscribe function the caller must run on teardown.
 */
export function subscribeThumbnailAvailable(listener: () => void): () => void {
  return thumbnailManager.subscribeThumbnailAvailable(listener);
}

// ── Refresh thunks (stateless — called by View-layer polling hook) ──

/**
 * On 10-foot devices, keep the thumbnail manager in sync with the player during
 * normal playback: bound its fetch window by the live/available seekable edge
 * (so forward prefetch never requests not-yet-broadcast thumbnails → 404s), and
 * track the playhead.  During active trick-play, refreshTrickPlayState drives
 * the position from the seek target at the faster 250 ms cadence, so the
 * playhead is not tracked here (this 1 s call must not overwrite it).
 * No-op off-TV or before thumbnails have loaded.
 */
function updateTvThumbnailPlayback(
  vpc: VideoPlaybackController,
  currentTime: number,
  seekRange: TSeekRange
): void {
  if (!DeviceModel.isTvDevice() || !thumbnailsLoaded) {
    return;
  }
  thumbnailManager.setSeekableBounds(seekRange.start, seekRange.end);
  if (!vpc.isTrickPlayActive()) {
    thumbnailManager.updateCurrentPosition(currentTime);
  }
}

/**
 * Banner title and ratings for VOD, read from its offer preview. The utils
 * are cached on the preview object so the 1 s poll does not rebuild them.
 */
function resolveVodBannerInfo(vodOfferPreview: TPreview | undefined): {
  parentalControlsRating: ReadonlyArray<Record<string, unknown>>;
  programDetails: Array<string>;
} {
  if (vodOfferPreview !== prevPreview) {
    prevAirings = undefined;
    prevPreview = vodOfferPreview;
    airingUtils =
      vodOfferPreview !== undefined
        ? new AiringPreviewUtils(undefined, vodOfferPreview)
        : null;
  }
  if (vodOfferPreview === undefined || airingUtils === null) {
    return { parentalControlsRating: [], programDetails: [""] };
  }
  return {
    parentalControlsRating: airingUtils.getParentalControlsRating() || [],
    programDetails: airingUtils.getSeasonEpisodeTitle()
  };
}

/** Start Over / Catch Up: the airing's length, else the player's span. nDVR has its own rule. */
function resolveSocuDuration(
  isNdvr: boolean,
  airingDuration: number,
  offerPreview: unknown,
  seekSpan: number
): number {
  if (isNdvr) {
    return resolveNdvrDuration(offerPreview, seekSpan);
  }
  if (airingDuration <= 0) {
    return seekSpan;
  }
  // Either the EPG airing or the stream's own padding can be the real end.
  return Math.max(airingDuration, seekSpan);
}

/** nDVR: the recording's own length, which a partial recording keeps shorter than its airing. */
function resolveNdvrDuration(offerPreview: unknown, seekSpan: number): number {
  const recordingDuration: unknown = readProperty(
    offerPreview,
    "recordingDuration"
  );
  // recordingDuration is fetched once at playback start, so an in-progress
  // (NDVR_LIVE) recording can grow its seekable span past that snapshot.
  return typeof recordingDuration === "number" && recordingDuration > 0
    ? Math.max(recordingDuration, seekSpan)
    : seekSpan;
}

/** VOD: the watch action's, then the preview's, then the player's; `previewDuration` is trailer length. */
function resolveVodDuration(
  watchVodAction: unknown,
  vodOfferPreview: unknown,
  seekSpan: number,
  mediaDuration: number
): number {
  const kernelDuration: unknown = readProperty(
    readProperty(watchVodAction, "kernel"),
    "duration"
  );
  if (typeof kernelDuration === "number" && kernelDuration > 0) {
    return Math.max(kernelDuration, seekSpan);
  }
  const previewDuration: unknown = readProperty(vodOfferPreview, "duration");
  if (typeof previewDuration === "number" && previewDuration > 0) {
    return Math.max(previewDuration, seekSpan);
  }
  return Math.max(mediaDuration, seekSpan);
}

/**
 * Reads playback telemetry from VPC and dispatches to Redux.
 * Called on interval by the View-layer usePlayerPolling hook.
 */
export const refreshPlaybackState =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

    const currentTime: number = vpc.getPlayerCurrentTime();
    const duration: number = vpc.getDuration();
    const seekRange: TSeekRange = vpc.getSeekRange();
    const playerState: PlayerState = lastKnownPlayerState;
    const isStartOverPlaying: boolean = vpc.isStartOver();
    // Catch Up is a VOD-kind SOCU stream: like Start Over its player clock is
    // absolute epoch seconds, so it shares the seekRange-derived timeline below.
    const isCatchUpPlaying: boolean = vpc.isCatchUp();
    // nDVR (in-progress or completed recording) shares the same elapsed/duration
    // timeline as Start Over / Catch Up rather than the live wall-clock one.
    const isNdvrPlaying: boolean = vpc.isNdvr();
    // VOD has no airing/preview metadata at all (see the final `else` branch
    // below), so its elapsed/duration timeline is built straight from
    // seekRange rather than from `airingUtils`.
    const isVodPlaying: boolean = vpc.isVod();

    updateTvThumbnailPlayback(vpc, currentTime, seekRange);

    // The seekable window decides the TMIS seek-bar range and can appear or
    // grow after playback starts (e.g. an in-progress recording); the trick-play
    // poll then carries any re-derived eligibility to the store.
    vpc.refreshSeekableWindowEligibility();

    dispatch(
      playbackStateUpdated({
        currentTime,
        duration,
        playerState,
        seekRange,
        isStartOverPlaying,
        isPositionSettled: hasFirstFramePlayed
      })
    );

    // ── Airing metadata (change-detected) ──
    const metadata = vpc.getMetadata();
    const airingOffers = metadata.airingOffers;
    // VOD metadata lands in vodOfferPreview rather than offerPreview.
    const offerPreview = metadata.offerPreview || metadata.vodOfferPreview;

    if (airingOffers !== undefined || offerPreview !== undefined) {
      if (airingOffers !== prevAirings || offerPreview !== prevPreview) {
        prevAirings = airingOffers;
        prevPreview = offerPreview;
        airingUtils = new AiringPreviewUtils(airingOffers, offerPreview);
        // TiVo+ is live-only, so start-over is unavailable regardless of what
        // the airing data advertises.
        const playing: IPlayable | null = vpc.getPlayable();
        const isTivoPlus: boolean =
          playing !== null &&
          playing.channel !== undefined &&
          ChannelsInteractor.isTivoPlusChannel(playing.channel);
        vpc.setStartOverAvailable(
          !isTivoPlus && airingUtils.getIsAvailableInSocu()
        );
      }
      if (airingUtils === null) {
        return;
      }

      // ── SOCU mode (Start Over / Catch Up): use seekRange values directly ──
      // seekRange.start/end may be epoch timestamps or seconds;
      // the seek bar calculation handles both formats consistently.
      // The total bar spans 0 to endTimeDuration (full program).
      // The filled portion spans seekRange.start to seekRange.end — for Start
      // Over that edge grows as the program airs, for Catch Up the whole
      // program is already available so it covers the full bar.
      if (isStartOverPlaying || isCatchUpPlaying || isNdvrPlaying) {
        const parentalControlsRating =
          airingUtils.getParentalControlsRating() || [];
        const programDetails = airingUtils.getSeasonEpisodeTitle();
        const endTimeDuration: number = resolveSocuDuration(
          isNdvrPlaying,
          airingUtils.getDuration(),
          offerPreview,
          seekRange.end - seekRange.start
        );
        const socuThumbnailEpochs = resolveSocuThumbnailEpochRange(
          airingUtils,
          seekRange,
          endTimeDuration
        );
        dispatch(
          airingInfoUpdated({
            startTimeLabel: "",
            endTimeLabel: formatElapsedTime(endTimeDuration),
            airingStartEpoch: seekRange.start,
            airingEndEpoch: seekRange.start + endTimeDuration,
            parentalControlsRating: parentalControlsRating,
            programDetails: programDetails
          })
        );
        tryLoadThumbnails(
          socuThumbnailEpochs.startEpoch,
          socuThumbnailEpochs.endEpoch
        );
        return;
      }

      // ── Live / IPTV mode: wall-clock labels ──
      const startTime: string = airingUtils.getStartTime();
      const endTime: Date = airingUtils.getEndTime();
      const startLabel: string =
        startTime !== "" ? formatAiringClockTime(startTime) : "";
      const endLabel: string = !isNaN(endTime.getTime())
        ? formatAiringClockTime(endTime.getTime())
        : "";
      const startEpoch: number =
        startTime !== "" && !isNaN(new Date(startTime).getTime())
          ? Math.floor(new Date(startTime).getTime() / 1000)
          : 0;
      const endEpoch: number = !isNaN(endTime.getTime())
        ? Math.floor(endTime.getTime() / 1000)
        : 0;

      const parentalControlsRating =
        airingUtils.getParentalControlsRating() || [];

      const programDetails = airingUtils.getSeasonEpisodeTitle();
      dispatch(
        airingInfoUpdated({
          startTimeLabel: startLabel,
          endTimeLabel: endLabel,
          airingStartEpoch: startEpoch,
          airingEndEpoch: endEpoch,
          parentalControlsRating: parentalControlsRating,
          programDetails: programDetails
        })
      );

      // ── Thumbnail loading (fires once when airing epochs are known) ──
      tryLoadLiveThumbnails(
        airingOffers as Array<Record<string, unknown>> | undefined,
        startEpoch,
        endEpoch
      );
    } else if (isVodPlaying) {
      // VOD has no airing metadata, so the timeline comes straight from
      // seekRange; only the title and ratings come from its offer preview.
      const vodInfo = resolveVodBannerInfo(metadata.vodOfferPreview);
      const endTimeDuration: number = resolveVodDuration(
        metadata.watchVodAction,
        metadata.vodOfferPreview,
        seekRange.end - seekRange.start,
        duration
      );
      dispatch(
        airingInfoUpdated({
          startTimeLabel: "",
          endTimeLabel: formatElapsedTime(endTimeDuration),
          airingStartEpoch: seekRange.start,
          airingEndEpoch: seekRange.start + endTimeDuration,
          parentalControlsRating: vodInfo.parentalControlsRating,
          programDetails: vodInfo.programDetails
        })
      );
      tryLoadThumbnails(seekRange.start, seekRange.start + endTimeDuration);
    } else {
      dispatch(
        airingInfoUpdated({
          startTimeLabel: "",
          endTimeLabel: "",
          airingStartEpoch: 0,
          airingEndEpoch: 0,
          parentalControlsRating: [],
          programDetails: [""]
        })
      );
    }
  };

/**
 * Determines whether the seek motion should stop because the target
 * position has reached a seekRange boundary. Reads all required state
 * from the VideoPlaybackController singleton.
 */
function shouldStopTrickPlaySeek(): boolean {
  const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
  const mgr: TrickPlayManager | null = vpc.getTrickPlayManager();
  if (mgr === null) {
    return false;
  }
  const targetPosition: number = vpc.getTrickPlayTargetPosition();
  const seekDirection: Direction | null = mgr.getCurrentDirection();
  const range = vpc.getSeekRange();
  const lowerBound: number =
    typeof mgr.getEffectiveLowerBound === "function"
      ? mgr.getEffectiveLowerBound()
      : range.start;

  let boundaryThreshold: number = 0;
  if (typeof mgr.getConfig === "function") {
    const config = mgr.getConfig();
    if (config.boundaryThreshold > 0) {
      boundaryThreshold = config.boundaryThreshold;
    }
  }

  const nearRangeStart: boolean =
    targetPosition <= lowerBound + boundaryThreshold;
  const nearRangeEnd: boolean = targetPosition >= range.end - boundaryThreshold;
  const isForward: boolean = seekDirection === Direction.Forward;
  const isBackward: boolean = seekDirection === Direction.Backward;
  return (
    (isForward && nearRangeEnd) ||
    (isBackward && nearRangeStart) ||
    (seekDirection === null && (nearRangeStart || nearRangeEnd))
  );
}

/**
 * Checks whether a trick-play seek to `targetPosition` crosses the
 * Live→StartOver seekable boundary and, if so, switches content type
 * instead of letting the caller perform its normal seek.
 *
 * Only the Live→StartOver (backward) direction switches content type here.
 * StartOver/SOCU never auto-switches to Live from a seek gesture — going
 * live is only ever the explicit "Go to Live" action (see `goToLive()`).
 * At the StartOver forward boundary this returns unhandled so the caller's
 * normal seek runs, and TrickPlayManager's own boundary clamp stops it there.
 *
 * @param targetPosition - Position the skip/scrub/confirm would land on.
 * @param resumeBasePositionSeconds - Resume-base position to pass through
 * to switchPlaybackContentType's second argument when switching backward
 * into Start Over. Only scrubComplete needs this — its playhead hasn't
 * moved to the target yet, unlike skip/confirm which rely on VPC's own
 * current/target position.
 */
function checkTrickPlayBoundarySwitch(
  targetPosition: number,
  resumeBasePositionSeconds?: number
): { boundaryHandled: boolean } {
  const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
  const range: TSeekRange = vpc.getSeekRange();

  // Live: beyond seekRange.start → switch to Start Over
  if (!vpc.isStartOver() && !vpc.isSocu()) {
    const tpm: TrickPlayManager | null = vpc.getTrickPlayManager();
    if (
      tpm !== null &&
      tpm.getEligibility().startOverAvailable &&
      targetPosition < range.start
    ) {
      if (vpc.getMediaPaneType() === MediaPaneType.TMIS) {
        if (resumeBasePositionSeconds !== undefined) {
          vpc.switchPlaybackContentType(
            PlaybackContentType.STARTOVER_RESUME,
            resumeBasePositionSeconds
          );
        } else {
          vpc.switchPlaybackContentType(PlaybackContentType.STARTOVER_RESUME);
        }
      }
      return { boundaryHandled: true };
    }
  }

  return { boundaryHandled: false };
}

/**
 * Reads trick-play state from VPC and dispatches to Redux.
 * Called on interval by the View-layer usePlayerPolling hook.
 */
export const refreshTrickPlayState =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const active: boolean = vpc.isTrickPlayActive();
    const mode: TrickPlayMode | null = vpc.getTrickPlayMode();

    let targetPosition: number = 0;
    let seekDirection: Direction | null = null;
    let currentSpeedLevel: SpeedLevel | null = null;
    let seekEntryPoint: number | null = null;
    const mgr: TrickPlayManager | null = vpc.getTrickPlayManager();
    if (mgr !== null && mgr.isActive()) {
      targetPosition = vpc.getTrickPlayTargetPosition();
      seekDirection = mgr.getCurrentDirection();
      currentSpeedLevel = mgr.getCurrentLevel();
      seekEntryPoint = mgr.getSeekEntryPoint();

      // Boundary detection: stop seek motion when target crosses
      // seekRange boundary. Session stays active (thumbnail visible),
      // speed indicator dismissed. User must confirm to switch stream.
      if (currentSpeedLevel !== null && shouldStopTrickPlaySeek()) {
        vpc.trickPlayHoldRelease();
        currentSpeedLevel = null;
      }
    }

    dispatch(
      trickPlayStateUpdated({
        isTrickPlayActive: active,
        trickPlayMode: mode,
        targetPosition,
        seekDirection,
        currentSpeedLevel,
        seekEntryPoint
      })
    );

    const eligibility = vpc.getTrickPlayEligibility();
    dispatch(
      trickPlayEligibilityUpdated({
        eligibility: eligibility === null ? DEFAULT_ELIGIBILITY : eligibility,
        restrictionEnabled: vpc.isTrickPlayRestrictionEnabled()
      })
    );

    // Suspended for the existing no-network case (isTrickPlayRestrictionEnabled
    // false) as well as any other playback error OSD/Dialog now covering the
    // player — neither should keep fetching thumbnail images in the
    // background while the viewer can't interact with trick-play anyway.
    // Applies to both platforms: ThumbnailManager.setFetchSuspended() covers
    // the 10-foot strategy directly and delegates to BrowserThumbnailStrategy
    // otherwise.
    const state: UnityRootState = getState();
    const isPlaybackErrorOsdVisible: boolean =
      selectIsOsdVisible(state) &&
      selectOsdPriority(state) >= ErrorPriority.GENERAL_RESTRICTIONS;
    const isPlaybackErrorOverlayVisible: boolean =
      isPlaybackErrorOsdVisible || selectIsUnityDialogVisible(state);
    thumbnailManager.setFetchSuspended(
      !vpc.isTrickPlayRestrictionEnabled() || isPlaybackErrorOverlayVisible
    );

    if (DeviceModel.isTvDevice()) {
      // Belt-and-braces direction + speed push: onSeekStarted covers hold,
      // skip and speed-cycle, but both also change by routes that never call
      // it — the boundary auto-stop above, holdRelease, and a counter-direction
      // speed-cycle stepping the level down inside TrickPlayManager. Pushing
      // the MODEL's own values makes any divergence self-correct within one
      // tick rather than persisting for the whole session. Both setters
      // early-return on an unchanged value, so this 4 Hz push is free in the
      // common case.
      //
      // Direction before speed: the speed re-seed reads the active direction,
      // so correcting the direction first avoids seeding the look-ahead the
      // wrong way and immediately rebuilding it.
      thumbnailManager.setSeekDirection(active ? seekDirection : null);
      thumbnailManager.setSeekVelocity(active ? currentSpeedLevel : null);
      // Keep the fetch window bounded by the live/available edge during
      // trick-play too (the edge keeps advancing while the user scrubs).
      if (thumbnailsLoaded) {
        const tpSeekRange: TSeekRange = vpc.getSeekRange();
        thumbnailManager.setSeekableBounds(tpSeekRange.start, tpSeekRange.end);
      }
      // Drive the thumbnail look-ahead from the seek target at 250 ms cadence
      // while trick-play is active.  Using targetPosition here (not the player's
      // current time) ensures extend10FootDirectionalBatch() walks ahead of where
      // the user is actually seeking, eliminating stale-neighbour fallbacks.
      if (active && mgr !== null && mgr.isActive() && targetPosition > 0) {
        thumbnailManager.updateCurrentPosition(targetPosition);
      }
    }
  };

// ── Lifecycle thunks ──

/**
 * Subscribes to VPC player state listener for instant state change push.
 * Dispatched from FullScreenPlayback mount useEffect.
 *
 * Polling timers are owned by the View-layer usePlayerPolling hook —
 * this thunk only sets up the event-driven subscription.
 */
export const subscribePlayerStateListener =
  () =>
  (dispatch: UnityAppDispatch): void => {
    // Guard against double-subscribe
    cleanupPlayerStateListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<PlayerState> =
      vpc.getPlayerStateChangeListener();

    const handleStateChange = (state: PlayerState): void => {
      lastKnownPlayerState = state;
      // Track the position-settle point: a new load (LOADING) resets the latch;
      // the first frame (where playback begins, and where onFirstFrame applies
      // the Start Over resume seek) settles it. Applies to all playback types.
      if (state === PlayerState.LOADING) {
        hasFirstFramePlayed = false;
      } else if (state === PlayerState.FIRST_FRAME) {
        hasFirstFramePlayed = true;
      }
      // Track readiness follows the actual combined audio + CC/subtitle
      // track count rather than the player lifecycle state — only
      // re-dispatch when that count changes, so the button isn't toggled
      // off and on again for state transitions that don't affect tracks.
      const mediaTracksLength: number =
        vpc.getAudioTrackOptions().length + vpc.getTextTrackOptions().length;
      if (mediaTracksLength !== lastMediaTracksLength) {
        lastMediaTracksLength = mediaTracksLength;
        dispatch(mediaTracksReadyUpdated(mediaTracksLength !== 0));
      }
      dispatch(
        playbackStateUpdated({
          currentTime: vpc.getPlayerCurrentTime(),
          duration: vpc.getDuration(),
          playerState: state,
          seekRange: vpc.getSeekRange(),
          isStartOverPlaying: vpc.isStartOver(),
          isPositionSettled: hasFirstFramePlayed
        })
      );
      // Drive the thumbnail-fetch debounce on ALL platforms: the manager
      // branches internally (TV → 10-foot initial preload, browser → eager
      // prefetch), so both share the same LOADED/FIRST_FRAME trigger.
      // FIRST_FRAME implies LOADED has already occurred.  Calling
      // notifyPlayerLoaded() for both states ensures the debounce fires
      // correctly even in resume/re-mount scenarios where the immediate-sync
      // path delivers FIRST_FRAME as the current state without a prior LOADED
      // event being seen by this listener.
      if (state === PlayerState.LOADED || state === PlayerState.FIRST_FRAME) {
        thumbnailManager.notifyPlayerLoaded();
      }
      if (state === PlayerState.FIRST_FRAME) {
        thumbnailManager.notifyFirstFrame();
      }
    };

    listener.subscribe(handleStateChange);
    stateChangeUnsubscribe = (): void => {
      listener.unsubscribe(handleStateChange);
    };

    // Immediate sync: seed Redux with current player state so the UI
    // does not show stale IDLE after re-mount.
    const currentState: PlayerState | null = vpc.getPlayerState();
    if (currentState !== null) {
      // Remount mid-playback: the listener never saw FIRST_FRAME for this load,
      // so seed the settled latch from the current state (past first frame) or
      // accumulated play time, otherwise the knob would stay hidden.
      const isPastFirstFrame: boolean =
        currentState !== PlayerState.IDLE &&
        currentState !== PlayerState.LOADING &&
        currentState !== PlayerState.READY;
      if (isPastFirstFrame || vpc.getPlayerStats().playTime > 0) {
        hasFirstFramePlayed = true;
      }
      handleStateChange(currentState);
    }
  };

/**
 * Synchronous cleanup — unsubscribes listener and resets airing cache.
 * Callable from thunk or standalone (e.g. unmount cleanup).
 */
export function cleanupPlayerStateListener(): void {
  if (stateChangeUnsubscribe !== null) {
    stateChangeUnsubscribe();
    stateChangeUnsubscribe = null;
  }
  prevAirings = undefined;
  prevPreview = undefined;
  airingUtils = null;
  lastKnownPlayerState = PlayerState.IDLE;
  hasFirstFramePlayed = false;
  lastMediaTracksLength = 0;

  // Reset the thumbnail manager in place — see the declaration for why the
  // instance is never replaced. resetThumbnails() preserves m10FootIsActive
  // and the merged config, and enable10FootStrategy() no-ops once active, so
  // the strategy survives content transitions without re-arming.
  thumbnailManager.resetThumbnails();
  if (DeviceModel.isTvDevice()) {
    // Size the sliding-window cache to the device. enable10FootStrategy() is a
    // no-op once active, so the tier is resolved and applied exactly once, on
    // the first mount. An unknown memory report on a TV resolves to "low" —
    // see getDeviceMemoryTier() for why that default is deliberate.
    const memoryTier: TDeviceMemoryTier = getDeviceMemoryTier();
    // Logged because the tier is invisible in the UI and decides the cache
    // size, concurrency and prefetch depth a field unit actually ran with —
    // without it, a thumbnail report from a device is not interpretable.
    Log.i(TAG, "Thumbnail 10-foot memory tier=" + memoryTier);
    thumbnailManager.enable10FootStrategy(
      memoryTier === "low" ? LOW_MEMORY_10FOOT_OVERRIDES : undefined
    );
  }
  thumbnailsLoaded = false;
  thumbnailSourceKey = null;
  thumbnailPlayableIdentity = null;
}

/**
 * Unsubscribes listener, resets slice state.
 * Dispatched from FullScreenPlayback unmount useEffect.
 */
export const unsubscribePlayerStateListener =
  () =>
  (dispatch: UnityAppDispatch): void => {
    cleanupPlayerStateListener();
    dispatch(videoPlayerUiReset());
  };

/**
 * Subscribes to VPC playable change listener for playable state updates.
 * Dispatched from FullScreenPlayback mount useEffect.
 */
export const subscribePlayableChangeListener =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    // Guard against double-subscribe
    cleanupPlayableChangeListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<IPlayable> = vpc.getPlayableChangeListener();

    const handlePlayableChange = (playable: IPlayable): void => {
      // An in-player channel change (CH+/CH-, number entry, mini-guide) keeps
      // FullScreenPlayback mounted, so the unmount reset never runs, and the
      // refreshPlaybackState poll that would otherwise notice only runs while
      // the controls are visible. Release here so the previous content's
      // thumbnails are never served — nor fetched by the preload the new
      // stream's LOADED/FIRST_FRAME re-arms. VPC notifies before play(), so
      // those events still arrive afterwards and re-arm the debounce cleanly.
      if (
        thumbnailPlayableIdentity !== null &&
        getThumbnailPlayableIdentity(playable) !== thumbnailPlayableIdentity
      ) {
        releaseThumbnails("playable changed");
      }
      dispatch(playableUpdated({ playable }));
      dispatch(refreshActionKernelTypes());
      const state: UnityRootState = getState();
      const isOsdActive: boolean = selectIsOsdVisible(state);
      const isDialogActive: boolean = selectIsUnityDialogVisible(state);
      if (isOsdActive || isDialogActive) {
        dispatch(dismissError());
      }
    };

    listener.subscribe(handlePlayableChange);
    playableChangeUnsubscribe = (): void => {
      listener.unsubscribe(handlePlayableChange);
    };

    // Dispatch initial playable state
    const initialPlayable: IPlayable | null = vpc.getPlayable();
    dispatch(playableUpdated({ playable: initialPlayable }));
    dispatch(refreshActionKernelTypes());
  };

/**
 * Synchronous cleanup — unsubscribes playable listener.
 * Callable from thunk or standalone (e.g. unmount cleanup).
 */
export function cleanupPlayableChangeListener(): void {
  if (playableChangeUnsubscribe !== null) {
    playableChangeUnsubscribe();
    playableChangeUnsubscribe = null;
  }
}

/**
 * Unsubscribes playable listener.
 * Dispatched from FullScreenPlayback unmount useEffect.
 */
export const unsubscribePlayableChangeListener =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    cleanupPlayableChangeListener();
  };

/**
 * Subscribes to VPC metadata-state listener so views can react to
 * PREVIEW_READY and other metadata lifecycle transitions.
 *
 * Only a metadata failure that blocks playback for the current content type
 * (see `VideoPlaybackController.isMetadataErrorBlocking`) surfaces through
 * `displayError` — mirrors `subscribeSessionStateChangeListener`'s
 * error-only dispatch. A following `*_READY` dismisses that dialog only when
 * it is still showing the metadata error code (V555) — e.g. NetworkGuard's
 * retry-on-reconnect can resolve the fetch and start playback after the
 * dialog already fired, and nothing else would clear it. Unrelated dialogs
 * (DRM/session, a different code) are left alone.
 * Non-blocking failures (e.g. LIVE preview, NDVR last-viewed-position) are
 * logged only, so playback continues silently.
 */
export const subscribeMetadataStateListener =
  () =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    cleanupMetadataStateListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<MetadataState> =
      vpc.getMetadataStateChangeListener();

    const handleMetadataStateChange = (metadataState: MetadataState): void => {
      dispatch(metadataStateUpdated({ metadataState }));
      dispatch(refreshActionKernelTypes());

      if (metadataState.endsWith("_ERROR")) {
        const error: PlaybackError = vpc.getMetadataError();
        if (!vpc.isMetadataErrorBlocking(metadataState)) {
          Log.e(
            TAG,
            "handleMetadataStateChange() - non-blocking",
            metadataState,
            error
          );
          return;
        }
        dispatch(
          displayError({
            code: error.getCode(),
            message: error.getDescription(),
            title: error.getTitle()
          })
        );
        return;
      }

      if (metadataState.endsWith("_READY")) {
        const state: UnityRootState = getState();
        if (
          selectIsUnityDialogVisible(state) &&
          selectUnityDialogErrorCode(state) === GenericErrorCode.UNKNOWN_ERROR
        ) {
          dispatch(dismissError());
        }
      }
    };

    listener.subscribe(handleMetadataStateChange);
    metadataStateUnsubscribe = (): void => {
      listener.unsubscribe(handleMetadataStateChange);
    };

    const initialMetadataState: MetadataState = vpc.getMetadataState();
    dispatch(metadataStateUpdated({ metadataState: initialMetadataState }));
    dispatch(refreshActionKernelTypes());
  };

/**
 * Synchronous cleanup — unsubscribes metadata-state listener.
 */
export function cleanupMetadataStateListener(): void {
  if (metadataStateUnsubscribe !== null) {
    metadataStateUnsubscribe();
    metadataStateUnsubscribe = null;
  }
}

/**
 * Unsubscribes metadata-state listener.
 */
export const unsubscribeMetadataStateListener =
  () =>
  (dispatch: UnityAppDispatch): void => {
    cleanupMetadataStateListener();
    dispatch(dismissError());
  };

function getPreviousScreen(getState: () => UnityRootState): string {
  const state: UnityRootState = getState();
  const previousScreen: string = selectPreviousUrl(state);
  return previousScreen;
}

/**
 * Resolves where a playback error overlay (dialog or OSD) should send the
 * user once it's dismissed: back to Guide or Search if that's where playback
 * was launched from, otherwise Home.
 */
function resolvePlaybackErrorExitUrl(getState: () => UnityRootState): string {
  const previousScreen: string = getPreviousScreen(getState);
  if (
    previousScreen === LocationUrls.GUIDE ||
    previousScreen === LocationUrls.SEARCH
  ) {
    return previousScreen;
  }
  return LocationUrls.HOME_SCREEN;
}

/**
 * Builds the dialog's "OK" action for a playback error — dismisses the
 * dialog and navigates to Guide/Search/Home per `resolvePlaybackErrorExitUrl`.
 */
function buildPlaybackErrorActions(
  getState: () => UnityRootState,
  navigateBackOrTo: (url: string) => void
): UnityDialogActionConfig[] | undefined {
  return [
    {
      id: "ok",
      label: Locale.getString(LocaleKeys.ALERT_ACTION_OK),
      onPress: (): void => {
        navigateBackOrTo(resolvePlaybackErrorExitUrl(getState));
      }
    }
  ];
}

/**
 * Builds the `onBack` callback for a playback-error dialog — same
 * destination as `buildPlaybackErrorActions`' OK button, so BACK exits the
 * player instead of UnityDialogController's default plain-cancel. Shared by
 * every `displayError()` call site in this file that opens a dialog-routed
 * player error (raw playback errors, grace-period-ended, session errors).
 */
function buildPlaybackErrorOnBack(
  getState: () => UnityRootState,
  navigateBackOrTo: (url: string) => void
): () => void {
  return (): void => {
    navigateBackOrTo(resolvePlaybackErrorExitUrl(getState));
  };
}

/**
 * exitPlaybackErrorOsd — dismisses the error-routed OSD and navigates to
 * Guide/Home, exactly like pressing the playback-error dialog's OK action.
 *
 * The OSD has no modal of its own to intercept BACK (unlike the dialog,
 * which UnityDialogController owns), so FullScreenPlayback dispatches this
 * directly when BACK is pressed while a playback error is showing on the OSD
 * surface.
 */
export const exitPlaybackErrorOsd =
  (navigateBackOrTo: (url: string) => void) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    dispatch(dismissError());
    navigateBackOrTo(resolvePlaybackErrorExitUrl(getState));
  };

/**
 * Subscribes to the VPC playback-error listener and routes errors through the
 * shared error-display framework.
 *
 * @param navigateTo - Navigation callback from `useNavigationService`, used to
 * return to Guide (or Home) — whether the error dialog's OK button is
 * pressed or BACK is pressed to dismiss it — when Guide was the previous
 * screen.
 * @param navigateBackOrTo - Back-navigation callback from
 * `useNavigationService`, used instead of `navigateTo` when that screen is
 * already directly behind the player in history.
 */
export const subscribePlaybackErrorListener =
  (navigateBackOrTo: (url: string) => void) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    cleanupPlaybackErrorListener();
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<PlaybackError> =
      vpc.getPlaybackErrorListener();
    // BACK dismisses the dialog the same way OK does — otherwise
    // UnityDialogController's default BACK handling would just cancel the
    // dialog and strand the user on a player that isn't playing anything.
    const onBack = buildPlaybackErrorOnBack(getState, navigateBackOrTo);
    const existingError = vpc.getPlayerError();
    if (existingError != null) {
      dispatch(
        displayError({
          code: existingError.getCode(),
          message: existingError.getDescription(),
          title: existingError.getTitle(),
          actions: buildPlaybackErrorActions(getState, navigateBackOrTo),
          onBack
        })
      );
    }

    const handlePlaybackError = (error: PlaybackError): void => {
      Log.e(TAG, "handlePlaybackError()", error);
      dispatch(
        displayError({
          code: error.getCode(),
          message: error.getDescription(),
          title: error.getTitle(),
          actions: buildPlaybackErrorActions(getState, navigateBackOrTo),
          onBack
        })
      );
    };

    listener.subscribe(handlePlaybackError);
    playbackErrorUnsubscribe = (): void => {
      listener.unsubscribe(handlePlaybackError);
    };

    const initialPlaybackError: PlaybackError | null = vpc.getPlayerError();
    if (initialPlaybackError !== null) {
      handlePlaybackError(initialPlaybackError);
    }
  };

/**
 * Synchronous cleanup — unsubscribes playback-error listener.
 */
export function cleanupPlaybackErrorListener(): void {
  if (playbackErrorUnsubscribe !== null) {
    playbackErrorUnsubscribe();
    playbackErrorUnsubscribe = null;
  }
}

/**
 * Unsubscribes playback-error listener.
 */
export const unsubscribePlaybackErrorListener =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    cleanupPlaybackErrorListener();
  };

/**
 * Subscribes to the VPC exit-to-previous-screen listener. The model fires this
 * when full-screen playback must leave the player — a long-background return
 * (Catchup / nDVR / VOD) or a VOD / nDVR program reaching its end; the handler
 * raises a one-shot Redux flag the View acts on. Keeping the
 * subscription here (not in the component) preserves the View → ViewModel →
 * Model boundary used by the other player listeners.
 */
export const subscribeExitToPreviousScreenListener =
  () =>
  (dispatch: UnityAppDispatch): void => {
    // Guard against double-subscribe
    cleanupExitToPreviousScreenListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<void> = vpc.getExitToPreviousScreenListener();

    const handleExitToPreviousScreen = (): void => {
      dispatch(exitToPreviousScreenRequested());
    };

    listener.subscribe(handleExitToPreviousScreen);
    exitToPreviousScreenUnsubscribe = (): void => {
      listener.unsubscribe(handleExitToPreviousScreen);
    };
  };

/**
 * Synchronous cleanup — unsubscribes the exit-to-previous-screen listener.
 */
export function cleanupExitToPreviousScreenListener(): void {
  if (exitToPreviousScreenUnsubscribe !== null) {
    exitToPreviousScreenUnsubscribe();
    exitToPreviousScreenUnsubscribe = null;
  }
}

/**
 * Unsubscribes the exit-to-previous-screen listener.
 */
export const unsubscribeExitToPreviousScreenListener =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    cleanupExitToPreviousScreenListener();
  };

/**
 * Maps a location-based playback restriction to the OSD message shown to the
 * user. Returns `null` for restriction types that do not warrant an OSD from
 * this handler (the shared `osd` slice's priority guard covers the rest).
 */
function getRestrictionOsdMessage(
  type: PlaybackRestrictionType,
  playable: IPlayable | null
): string | null {
  if (type === PlaybackRestrictionType.OUT_OF_HOME) {
    let playbackContentType = "";
    if (playable && playable.playbackContentType) {
      playbackContentType = playable.playbackContentType;
    }
    const message = Locale.getString(
      LocaleKeys.PLAYER_OUT_OF_HOME_MESSAGE
    ).replace("{{playbackType}}", playbackContentType);
    return message;
  }
  if (type === PlaybackRestrictionType.OUT_OF_REGION) {
    return Locale.getString(LocaleKeys.PLAYER_OUT_OF_REGION_MESSAGE);
  }
  if (type === PlaybackRestrictionType.BLACKLISTED) {
    const urlError = Branding.getBrandingUiInfoForKey(
      BrandingUiInfoKeys.URL_ERROR
    ) as string;
    const deviceNameShort = Branding.getBrandingUiInfoForKey(
      BrandingUiInfoKeys.DEVICE_NAME_SHORT
    ) as string;
    const messageWithIPAddress = Locale.getString(
      LocaleKeys.PLAYER_BLACKLISTED_MESSAGE
    ).replace("{{existingIp}}", DeviceModel.getTVProperties().getIpAddress());
    const messageWithDeviceNameShort = messageWithIPAddress.replace(
      "{{deviceNameShort}}",
      deviceNameShort
    );
    const messageWithContactURL = messageWithDeviceNameShort.replace(
      "{{urlError}}",
      urlError
    );
    return messageWithContactURL;
  }
  if (type === PlaybackRestrictionType.UNSUBSCRIBED) {
    let channelName = "This channel";
    if (playable && playable.channel && playable.channel.name) {
      channelName = playable.channel.name;
    }
    const msoName = Branding.getBrandingUiInfoForKey(
      BrandingUiInfoKeys.MSO_NAME
    ) as string;
    const messageWithMSO = Locale.getString(
      LocaleKeys.PLAYER_UNSUBSCRIBED_MESSAGE
    ).replace("{{yourCableProvider}}", msoName);
    return messageWithMSO.replace("{{channelName}}", channelName);
  }
  if (type === PlaybackRestrictionType.DEVICE_RESTRICTED) {
    return Locale.getString(LocaleKeys.PLAYER_DEVICE_RESTRICTED_MESSAGE);
  }
  return null;
}

/**
 * Maps a playback restriction to its error code.
 *
 * Codes match kaveri-web-app so the same condition carries the same code on
 * both clients and can be looked up in the shared error dictionary. The code is
 * also what selects the display surface and priority — see
 * `resolveErrorSurface` — so this function no longer decides priority itself.
 *
 * Returns `null` for restriction types this handler does not surface; those are
 * paired 1:1 with `getRestrictionOsdMessage` returning `null`.
 */
function getRestrictionErrorCode(type: PlaybackRestrictionType): string | null {
  if (type === PlaybackRestrictionType.OUT_OF_HOME) {
    return RestrictionErrorCode.OUT_OF_HOME;
  }
  if (type === PlaybackRestrictionType.OUT_OF_REGION) {
    return RestrictionErrorCode.OUT_OF_REGION;
  }
  if (type === PlaybackRestrictionType.BLACKLISTED) {
    return RestrictionErrorCode.BLACKLISTED;
  }
  if (type === PlaybackRestrictionType.UNSUBSCRIBED) {
    return RestrictionErrorCode.UNSUBSCRIBED;
  }
  if (type === PlaybackRestrictionType.DEVICE_RESTRICTED) {
    return RestrictionErrorCode.DEVICE_RESTRICTED;
  }
  return null;
}

/**
 * Subscribes to the VPC restriction listener and surfaces the OUT_OF_HOME,
 * OUT_OF_REGION, BLACKLISTED, and UNSUBSCRIBED events as branded OSD messages
 * via the shared `osd` Redux slice.
 *
 * Kept on the ViewModel side (not the component) so the View → ViewModel →
 * Model boundary matches the other player listeners (state, playable,
 * metadata, exit-to-previous-screen). Routing goes through `displayError`, so
 * the error-display framework picks the surface and priority from the code and
 * arbitrates against competing errors (e.g. the network-error OSD).
 */
export const subscribePlaybackRestrictionListener =
  (navigateBackOrTo: (url: string) => void) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    // Guard against double-subscribe
    cleanupPlaybackRestrictionListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<PlaybackRestrictionType> =
      vpc.getRestrictionListener();

    const handleRestrictionChange = (type: PlaybackRestrictionType): void => {
      // When the in-home location grace period expires, surface the C718
      // error overlay — mirrors kaveri VideoPlayer.handleLocationUnreachable().
      if (
        type === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE &&
        vpc.isGracePeriodFinished()
      ) {
        const rawMessage = Branding.applyCustomerSupportPlaceholders(
          Locale.getString(LocaleKeys.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_MESSAGE)
        );
        dispatch(
          displayError({
            code: PlaybackSetupErrorCode.GRACE_PERIOD_ENDED,
            message: rawMessage,
            title: Locale.getString(
              LocaleKeys.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_TITLE
            ),
            actions: buildPlaybackErrorActions(getState, navigateBackOrTo),
            onBack: buildPlaybackErrorOnBack(getState, navigateBackOrTo)
          })
        );
        return;
      }

      const playable: IPlayable | null = vpc.getPlayable();
      const message: string | null = getRestrictionOsdMessage(type, playable);
      const code: string | null = getRestrictionErrorCode(type);
      if (message === null || code === null) {
        return;
      }
      // Surface and priority both come from the code — see
      // `resolveErrorSurface`. This handler only supplies the identity and the
      // localized text.
      dispatch(displayError({ code, message }));
    };

    listener.subscribe(handleRestrictionChange);
    playbackRestrictionUnsubscribe = (): void => {
      listener.unsubscribe(handleRestrictionChange);
    };
  };

/**
 * Synchronous cleanup — unsubscribes the restriction listener and hides
 * any restriction OSD this handler may have surfaced. Callable from thunk
 * or standalone (e.g. unmount cleanup).
 */
export function cleanupPlaybackRestrictionListener(): void {
  if (playbackRestrictionUnsubscribe !== null) {
    playbackRestrictionUnsubscribe();
    playbackRestrictionUnsubscribe = null;
  }
}

/**
 * Subscribes to VPC session-state listener so views can react to
 * SUCCESS, ERROR, and UNREACHABLE session lifecycle transitions.
 * Dispatched from FullScreenPlayback mount useEffect.
 */
export const subscribeSessionStateChangeListener =
  (navigateBackOrTo: (url: string) => void) =>
  (dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    cleanupSessionStateChangeListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<SessionState> =
      vpc.getSessionStateChangeListener();

    const handleSessionError = (sessionState: SessionState): void => {
      if (sessionState === SessionState.ERROR) {
        const vpc: VideoPlaybackController =
          VideoPlaybackController.getInstance();
        const sessionError = vpc.getSessionError();
        Log.e(TAG, "handleSessionError() - " + String(sessionError.type));
        if (sessionError.data !== undefined && sessionError.data !== null) {
          dispatch(
            displayError({
              code: sessionError.data.getCode(),
              message: sessionError.data.getMessage(),
              title: sessionError.data.getTitle(),
              actions: buildPlaybackErrorActions(getState, navigateBackOrTo),
              onBack: buildPlaybackErrorOnBack(getState, navigateBackOrTo)
            })
          );
        }
      }
    };

    listener.subscribe(handleSessionError);
    sessionStateChangeUnsubscribe = (): void => {
      listener.unsubscribe(handleSessionError);
    };

    const initialSessionState: SessionState = vpc.getSessionState();
    dispatch(sessionStateUpdated({ sessionState: initialSessionState }));
  };

/**
 * Synchronous cleanup — unsubscribes the session-state listener.
 */
export function cleanupSessionStateChangeListener(): void {
  if (sessionStateChangeUnsubscribe !== null) {
    sessionStateChangeUnsubscribe();
    sessionStateChangeUnsubscribe = null;
  }
}

/**
 * Unsubscribes the session-state listener.
 * Dispatched from FullScreenPlayback unmount useEffect.
 */
export const unsubscribeSessionStateChangeListener =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    cleanupSessionStateChangeListener();
  };

/**
 * Unsubscribes the restriction listener and dismisses any restriction OSD so
 * a lingering message can't survive onto the next screen.
 */
export const unsubscribePlaybackRestrictionListener =
  () =>
  (dispatch: UnityAppDispatch): void => {
    cleanupPlaybackRestrictionListener();
    dispatch(dismissError());
  };

// ── Command thunks ──

/**
 * Activates full-screen mode + trick play on mount.
 *
 * Initializes the TrickPlayManager with a permissive default eligibility
 * so the seek bar is not interactive until the session-success path
 * derives the real eligibility from `trickModeRestrictions` and calls
 * `setTrickPlayRestrictionEnabled(true)` (PRD §7.1 TP-AP-1..4).
 */
export const mountFullScreenPlayback =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    Log.d(TAG, "mount — activating full-screen mode");
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const currentState: PlayerState | null = vpc.getPlayerState();
    vpc.mount();
    vpc.fullScreen();

    // Every way back to a live program (Back, Watch, a guide re-select) lands
    // here, and none of them re-prepares its metadata — so a recording changed
    // while away would leave the OSD on the old action list.
    vpc.refreshLiveActions();

    // Boot-time defaults only. Real eligibility + restriction-enabled flag
    // are applied by VideoPlaybackController.onSessionSuccess once the
    // session response is received.
    vpc.initTrickPlay(DEFAULT_TRICKPLAY_CONFIG, DEFAULT_ELIGIBILITY);

    // PiP return path: an intermediate screen (e.g. GuideScreen) may have
    // called vpc.atmospheric() in its cleanup while navigating back to
    // /player, which stops the stream on desktop. Detect and restore it here.
    if (isPiPReturnPending()) {
      setPiPReturnPending(false);
      if (currentState === null || currentState === PlayerState.IDLE) {
        // Stream was killed during PiP exit — restart from the saved playable.
        if (vpc.getPlayable() !== null) {
          Log.d(TAG, "mount (PiP return) — restoring stopped stream");
          vpc.restorePlayback();
        }
      } else if (
        (currentState === PlayerState.PAUSED ||
          currentState === PlayerState.READY) &&
        wasPlayingOnPiPEntry()
      ) {
        // Stream is loaded but paused (e.g. browser auto-paused on PiP entry).
        Log.d(TAG, "mount (PiP return) — resuming paused stream");
        vpc.resume();
        setPlayingOnPiPEntry(false);
      }
    }
  };

/**
 * Puts the player into PlayerViewMode.Hidden — paused and invisible, with the
 * media source, session and playhead retained.
 *
 * Dispatched by NavigationInterceptor when navigating to a screen that has no
 * player surface (My Hub, Settings, Search, ...). Runs *before* the outgoing
 * player screen unmounts, so the unmount cleanups can detect Hidden and skip
 * their own (destructive, on desktop) atmospheric switch.
 */
export const hidePlayback =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    Log.d(TAG, "hidePlayback — switching player to hidden");
    VideoPlaybackController.getInstance().hidden();
  };

/** Whether the player is currently in PlayerViewMode.Hidden. */
export function isPlaybackHidden(): boolean {
  return VideoPlaybackController.getInstance().isPlaybackHidden();
}

/** Whether the player holds a playable (one playing, or one being tuned). */
export function hasPlayable(): boolean {
  return VideoPlaybackController.getInstance().getPlayable() !== null;
}

/**
 * Switches player to atmospheric mode on unmount.
 *
 * Skipped when native browser PiP is active — the video must keep playing
 * while the browser PiP window is open. usePictureInPictureCoordinator
 * triggers a /player re-mount on leavepictureinpicture, which re-runs
 * mountFullScreenPlayback.
 *
 * Also skipped once the player is Hidden: the destination screen has no player
 * surface and atmospheric() would stop the stream on desktop, defeating the
 * resume-on-return behaviour.
 */
export const unmountFullScreenPlayback =
  () =>
  (_dispatch: UnityAppDispatch, getState: () => UnityRootState): void => {
    // A stale PiP-return-pending means PiP exited but /player never mounted
    // (e.g. navigation was interrupted). Clear it to avoid false restores.
    setPiPReturnPending(false);
    // Guard: the player was already hidden for a playerless destination.
    if (isPlaybackHidden()) {
      Log.d(TAG, "unmount skipped — player is hidden");
      return;
    }
    // Guard: do not switch to atmospheric while PiP is active.
    // isBrowserPiPActive supplements document.pictureInPictureElement to cover
    // the timing race where the DOM property is not yet set at cleanup time.
    if (document.pictureInPictureElement != null || isBrowserPiPActive()) {
      Log.d(TAG, "unmount skipped — PiP is active");
      return;
    }
    // Guard: a Guide PiG mini-player session keeps playing so GuideScreen can
    // take over the stream with PiG. atmospheric() would stop it on desktop
    // before GuideScreen mounts and calls vpc.pig().
    if (selectWasPiGEnteredFromMiniClick(getState())) {
      Log.d(TAG, "unmount skipped — Guide PiG mini-player session active");
      return;
    }
    Log.d(TAG, "unmount — switching player to atmospheric");
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    vpc.atmospheric();
  };

/**
 * Skips forward via trick play.
 * The model call activates trick-play and sets the direction;
 * the always-on 250 ms poll picks up both within one tick.
 * The View layer handles the 1.5 s delta-label flash locally.
 */
export const skipForward =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

    const tpm = vpc.getTrickPlayManager();
    const isForwardRestricted: boolean =
      tpm !== null && !tpm.getEligibility().allowForward;
    if (isForwardRestricted) {
      dispatch(
        showToastMessage(
          Locale.getString(LocaleKeys.PLAYBACK_NOT_FAST_FORWARDABLE_MESSAGE)
        )
      );
      return;
    }

    // Forward never auto-switches to Live — TrickPlayManager's own boundary
    // clamp stops the skip at the StartOver edge; going live is Go-to-Live only.
    const performed: boolean = vpc.trickPlaySkip(Direction.Forward);
    if (performed) {
      dispatch(skipOccurred(Direction.Forward));
      if (DeviceModel.isTvDevice()) {
        // Use the trick-play target after the skip, not the player's current
        // time (which hasn't moved yet).  This anchors the directional batch
        // and the position look-ahead to where the user actually lands.
        // Explicit null level: a discrete skip is not a speed session and must
        // keep the dense 12-second prefetch grid.
        thumbnailManager.onSeekStarted(
          Direction.Forward,
          vpc.getTrickPlayTargetPosition(),
          null
        );
      }
    }
  };

/**
 * Skips backward via trick play.
 */
export const skipBackward =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

    const eligibilityCheckTpm = vpc.getTrickPlayManager();
    const isBackwardRestricted: boolean =
      eligibilityCheckTpm !== null &&
      !eligibilityCheckTpm.getEligibility().allowBackward;
    if (isBackwardRestricted) {
      dispatch(
        showToastMessage(
          Locale.getString(LocaleKeys.PLAYBACK_NOT_REWINDABLE_MESSAGE)
        )
      );
      return;
    }

    // Live: skip beyond the seekable boundary → switch to Start Over
    const backwardStep: number =
      eligibilityCheckTpm !== null
        ? eligibilityCheckTpm.getConfig().backwardStep
        : 8;
    const targetAfterBackwardSkip: number =
      vpc.getPlayerCurrentTime() - backwardStep;
    if (checkTrickPlayBoundarySwitch(targetAfterBackwardSkip).boundaryHandled) {
      vpc.trickPlayCancel();
      dispatch(refreshTrickPlayState());
      return;
    }

    const performed: boolean = vpc.trickPlaySkip(Direction.Backward);
    if (performed) {
      dispatch(skipOccurred(Direction.Backward));
      if (DeviceModel.isTvDevice()) {
        // Use the trick-play target after the skip, not the player's current time.
        // Explicit null level — see skipForward.
        thumbnailManager.onSeekStarted(
          Direction.Backward,
          vpc.getTrickPlayTargetPosition(),
          null
        );
      }
    }
  };

/**
 * Toggles play / pause.
 */
export const togglePlayPause =
  (isPlaying: boolean) =>
  (_dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    if (isPlaying) {
      // forced=true: an explicit user pause, unlike background/network/
      vpc.pause(true);
    } else {
      vpc.resume();
    }
  };

/**
 * Returns the current player volume as a 0–100 integer, clamped to [0, 100].
 */
export const getPlayerVolume = (): number => {
  const raw: number = Math.round(
    VideoPlaybackController.getInstance().getVolume() * 100
  );
  return Math.max(0, Math.min(100, raw));
};

/**
 * Returns channel number and logo/call-sign text derived from the current
 * offer preview, for use when the playable itself has no channel (e.g. VOD).
 */
export const getPreviewChannelInfo = (): TPreview => {
  const vpc = VideoPlaybackController.getInstance();
  const preview =
    vpc.getMetadata().offerPreview || vpc.getMetadata().vodOfferPreview;
  return preview || ({} as TPreview);
};

/**
 * Sets the player volume.
 * @param volume - Volume level between 0 and 100. Values outside this range are clamped.
 */
export const setPlayerVolume =
  (volume: number) =>
  (_dispatch: UnityAppDispatch): void => {
    const clamped: number = Math.max(0, Math.min(100, volume));
    VideoPlaybackController.getInstance().setVolume(clamped / 100);
  };

/**
 * Completes a scrub interaction.
 * Seeks to a specific position. If the target is beyond the buffer
 * start during live playback and Start Over is available, triggers
 * a Start Over stream switch instead (Req 1).
 */
export const scrubComplete =
  (targetSeconds: number) =>
  (_dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

    // Live→StartOver: scrub beyond the buffer start → switch content type
    // instead of seeking (Req 1). Scrub is a UI-only preview — the playhead
    // has not moved to the target yet, so the scrub target itself is passed
    // as the resume base position. Scrubbing past the StartOver forward
    // boundary is not switched here — it just seeks (and clamps) normally.
    if (
      checkTrickPlayBoundarySwitch(targetSeconds, targetSeconds).boundaryHandled
    ) {
      return;
    }

    vpc.setPlayPosition(targetSeconds);
  };

/**
 * Switches to atmospheric + unmutes (used for back navigation).
 */
export const switchToAtmospheric =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    vpc.atmospheric();
    vpc.unmute();
  };

/**
 * Ends the pause-timeout dialog's SOCU/nDVR/VOD stream and resumes the
 * last-tuned LIVE channel before leaving the player
 */
export const exitFromPauseTimeout =
  () =>
  async (dispatch: UnityAppDispatch): Promise<void> => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    try {
      // Awaited so the exit navigation (which aborts inflight calls) can't
      // race the channel tune switchToLiveTv() just kicked off.
      await vpc.switchToLiveTv();
    } catch (error: unknown) {
      Log.e(TAG, "exitFromPauseTimeout()", error);
    }
    dispatch(exitToPreviousScreenRequested());
  };

/**
 * Notifies VideoPlaybackController that native browser Picture-in-Picture has
 * been entered. Switches the VPC view mode to PIP and resumes playback if the
 * browser auto-paused the video element on PiP entry.
 */
export const enterBrowserPictureInPictureMode =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    Log.d(TAG, "enterBrowserPictureInPictureMode — native browser PiP started");
    VideoPlaybackController.getInstance().enterBrowserPictureInPictureMode();
  };

/**
 * Notifies VideoPlaybackController that native browser PiP has been exited and
 * the player should return to full-screen. Reverts the view mode and marks the
 * next FullScreenPlayback mount as a PiP-return so playback is restored if an
 * intermediate screen unmount stopped the stream.
 */
export const exitBrowserPictureInPictureMode =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance().exitBrowserPictureInPictureMode();
  };

/**
 * Closes native browser PiP by stopping playback. Used when the user closes or
 * minimizes the PiP window without returning to the player.
 */
export const closeBrowserPictureInPictureMode =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    Log.d(TAG, "closeBrowserPictureInPictureMode — stopping playback");
    VideoPlaybackController.getInstance()
      .closeBrowserPictureInPictureMode()
      .catch((error: unknown): void => {
        Log.e(TAG, "closeBrowserPictureInPictureMode — stop failed", error);
      });
  };

/**
 * Clears native browser PiP state without scheduling a /player return or
 * stopping playback. Used when routing from PiP to Guide, where GuideScreen
 * immediately switches the player into PiG mode on mount.
 */
export const clearBrowserPictureInPictureMode =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance().clearBrowserPictureInPictureMode();
  };

/**
 * Returns true when a native browser PiP session is active, or a
 * requestPictureInPicture() call has been issued but not yet confirmed by the
 * browser. Native PiP entry is asynchronous, so there is a window after the
 * call where document.pictureInPictureElement and isBrowserPiPActive() are
 * both still false/null even though a takeover is in flight. Screens that own
 * vpc.atmospheric() cleanup on unmount (e.g. GuideScreen) must check this to
 * avoid stopping playback out from under an in-flight PiP takeover.
 */
export function isBrowserPictureInPictureTransitionActive(): boolean {
  return VideoPlaybackController.getInstance().isBrowserPictureInPictureTransitionActive();
}

/** Returns whether native browser PiP is supported on this device. */
export function isPictureInPictureSupported(): boolean {
  return VideoPlaybackController.getInstance().isPictureInPictureSupported();
}

/** Returns the shared playback <video> element, or null if unavailable. */
export function getPlaybackVideoElement(): HTMLVideoElement | null {
  return VideoPlaybackController.getInstance().getPlaybackVideoElement();
}

/** Returns the fallback timeout (ms) for resolving a PiP leave event. */
export function getPictureInPictureLeaveResolveTimeoutMs(): number {
  return VideoPlaybackController.getInstance().getPictureInPictureLeaveResolveTimeoutMs();
}

/** Heuristically resolves whether native PiP leave should return to the player. */
export function shouldReturnToPlayer(returnIntended: boolean): boolean {
  return VideoPlaybackController.getInstance().shouldReturnToPlayer(
    returnIntended
  );
}

/** Returns whether returning to the player is currently intended on PiP leave. */
export function isReturnToPlayerIntended(): boolean {
  return VideoPlaybackController.getInstance().isReturnToPlayerIntended();
}

/** Sets whether returning to the player is intended on the next PiP leave. */
export function setReturnToPlayerIntended(value: boolean): void {
  VideoPlaybackController.getInstance().setReturnToPlayerIntended(value);
}

/** Sets whether playback is transitioning from Guide to full-screen Player. */
export function setNavigatingToPlayer(value: boolean): void {
  VideoPlaybackController.getInstance().setNavigatingToPlayer(value);
}

/** Returns whether a PiP->Guide conversion is currently in flight. */
export function isPipToGuideConversionActive(): boolean {
  return VideoPlaybackController.getInstance().isPipToGuideConversionActive();
}

/** Sets whether a PiP->Guide conversion is currently in flight. */
export function setPipToGuideConversionActive(value: boolean): void {
  VideoPlaybackController.getInstance().setPipToGuideConversionActive(value);
}

/** Registers the handler invoked synchronously before a route navigation. */
export function registerBeforeNavigateHandler(
  handler: BeforeNavigateHandler
): void {
  VideoPlaybackController.getInstance().registerBeforeNavigateHandler(handler);
}

/** Unregisters a previously-registered before-navigate handler. */
export function unregisterBeforeNavigateHandler(
  handler: BeforeNavigateHandler
): void {
  VideoPlaybackController.getInstance().unregisterBeforeNavigateHandler(
    handler
  );
}

/** Notifies the registered before-navigate handler of an upcoming navigation. */
export function notifyBeforeNavigate(targetUrl: string | null): void {
  VideoPlaybackController.getInstance().notifyBeforeNavigate(targetUrl);
}

/**
 * Notifies VideoPlaybackController that native browser fullscreen has been entered.
 * Called as a side-effect from the fullscreenchange DOM event in useBrowserFullscreen.
 * Applies the browser-fullscreen CSS class and updates PlayerViewMode tracking.
 */
export const enterBrowserFullscreenMode =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance().browserFullScreen();
  };

/**
 * Notifies VideoPlaybackController that native browser fullscreen has been exited.
 * Called as a side-effect from the fullscreenchange DOM event in useBrowserFullscreen.
 * Reverts CSS class and PlayerViewMode back to FullScreen.
 */
export const exitBrowserFullscreenMode =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance().fullScreen();
  };

/**
 * Go LIVE action.
 *
 * SOCU (Start Over / Catch Up) → tunes the live programme on the same channel.
 * Everything else → jumps to the live edge (seek range end) via
 * TrickPlayManager.goToLive().
 */
export const goToLive =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();

    // SOCU → tune the live programme on the SAME channel. Neither variant has a
    // live edge to seek to inside its own timeline (Start Over trails it, Catch
    // Up is a past programme), so a full stream switch is required.
    if (vpc.isSocu()) {
      if (vpc.getMediaPaneType() === MediaPaneType.TMIS) {
        vpc.switchToLiveTv();
      }
      return;
    }

    vpc.trickPlayGoToLive();
  };

/**
 * Start Over action.
 *
 * Already playing SOCU (Start Over / Catch Up): plays the content from the
 * beginning of the programme — seeks to the start of the SOCU stream.
 * Live playback with startOverAvailable === true: switches to the dedicated
 * Start Over stream (SOCU).
 * Live playback with restartAvailable === true (and nDVR / VOD): same-stream
 * restart by jumping to the start of the time-shift buffer / content (seek to
 * seekRange.start).
 *
 * TMIS: both same-stream routes (SOCU and restart) are backward seeks, so when
 * rewind is restricted the button shows the rewind restriction message
 * instead. Switching to the Start Over stream is not a seek and is unaffected.
 */
export const startOverOrRestart =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const tpm: TrickPlayManager | null = vpc.getTrickPlayManager();
    if (tpm === null) {
      return;
    }
    const eligibility = tpm.getEligibility();
    const isRestartRewindRestricted: boolean =
      vpc.getMediaPaneType() === MediaPaneType.TMIS &&
      !eligibility.allowBackward;

    // Already playing SOCU → play from the beginning of the programme (Req 3).
    // trickPlayStartFromBuffer() only seeks, so resume() is needed as well:
    // without it a user who pressed the button while paused at a thumbnail seek
    // target would be left paused at the programme start.
    if (vpc.isSocu()) {
      if (isRestartRewindRestricted) {
        dispatch(
          showToastMessage(getRestrictionToastMessage(Direction.Backward))
        );
        return;
      }
      vpc.trickPlayStartFromBuffer();
      vpc.resume();
      return;
    }

    // Live playback with Start Over available → switch to SOCU stream (Req 1)
    // Same session is reused — only content type and URL change.
    if (eligibility.startOverAvailable) {
      if (vpc.getMediaPaneType() === MediaPaneType.TMIS) {
        vpc.switchPlaybackContentType(PlaybackContentType.STARTOVER_BEGINNING);
      }
    } else if (eligibility.restartAvailable) {
      if (isRestartRewindRestricted) {
        dispatch(
          showToastMessage(getRestrictionToastMessage(Direction.Backward))
        );
        return;
      }
      vpc.trickPlayStartFromBuffer();
    }
  };

// ── Long-press / FF / RW thunks ──

/**
 * Start a long-press variable-speed seek session (Workflow B).
 * Called when D-pad / Arrow Left/Right has been held past
 * `LONG_PRESS_THRESHOLD_MS`. Speed escalation (L1 → L2 → L3) is
 * driven by the View-layer controller using `SPEED_ESCALATION_MS`.
 */
export const holdStart =
  (direction: Direction, level: SpeedLevel) =>
  (dispatch: UnityAppDispatch): void => {
    const trickPlayManager: TrickPlayManager | null =
      VideoPlaybackController.getInstance().getTrickPlayManager();
    const isDirectionRestricted: boolean =
      trickPlayManager !== null &&
      ((direction === Direction.Forward &&
        !trickPlayManager.getEligibility().allowForward) ||
        (direction === Direction.Backward &&
          !trickPlayManager.getEligibility().allowBackward));

    if (isDirectionRestricted) {
      dispatch(showToastMessage(getRestrictionToastMessage(direction)));
      return;
    }

    dispatch(speedTriggerMethodUpdated(true));
    if (DeviceModel.isTvDevice()) {
      // The speed level scales prefetch depth, concurrency and grid stride.
      // Escalation re-dispatches holdStart, so L1 -> L2 -> L3 is covered here.
      thumbnailManager.onSeekStarted(
        direction,
        VideoPlaybackController.getInstance().getTrickPlayTargetPosition(),
        level
      );
    }
    VideoPlaybackController.getInstance().trickPlayHold(direction, level);
    // Eagerly sync Redux so isTrickPlayActive is true immediately,
    // without waiting for the next 250 ms poll tick. This ensures
    // the NAV_OK handler sees the session as active right away.
    dispatch(refreshTrickPlayState());
  };

/**
 * Commit a long-press / FF / RW trick-play session and resume playback
 * from the confirmed position (CONFIRM intent, Workflow F).
 */
export const trickPlayConfirm =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const targetPosition: number = vpc.getTrickPlayTargetPosition();

    // Live→StartOver: confirm beyond the buffer start → switch content type.
    // The player stays paused at the seek entry point in Thumbnail mode, so the
    // seeked target (not the player's current time) is passed as the Start
    // Over resume base — mirrors scrubComplete(). Confirming past the
    // StartOver forward boundary is not switched here — it just seeks.
    if (
      checkTrickPlayBoundarySwitch(targetPosition, targetPosition)
        .boundaryHandled
    ) {
      vpc.trickPlayCancel();
      dispatch(refreshTrickPlayState());
      return;
    }

    vpc.trickPlayConfirm();
    if (DeviceModel.isTvDevice()) {
      thumbnailManager.onSeekConfirmed(targetPosition);
      // Session summary — outstanding blob URLs and the fetch/displayed ratio
      // are the two numbers that diagnose low-memory kills on TV.
      thumbnailManager.logMemoryDiagnostics("trickplay-confirm");
    }
    // Eagerly sync Redux so isTrickPlayActive is false immediately,
    // without waiting for the next 250 ms poll tick. Prevents the
    // NAV_OK handler from seeing a stale active session after the
    // controls auto-hide and polling stops.
    dispatch(refreshTrickPlayState());
  };

/**
 * Pause the active seek motion but keep the session alive so the speed
 * indicator stays visible. Called on hold key-release; the user must then
 * press OK to confirm or BACK / controls-dismiss to cancel.
 */
export const holdRelease =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance().trickPlayHoldRelease();
  };

/**
 * Cancel an in-progress trick-play session and return to the seek
 * entry point (CANCEL intent, Workflow E). Wired to BACK on TV and
 * ESC in the browser.
 */
export const trickPlayCancel =
  () =>
  (dispatch: UnityAppDispatch): void => {
    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    if (DeviceModel.isTvDevice()) {
      thumbnailManager.onSeekConfirmed(vpc.getPlayerCurrentTime());
      thumbnailManager.logMemoryDiagnostics("trickplay-cancel");
    }
    vpc.trickPlayCancel();
    // Eagerly sync Redux so isTrickPlayActive is false immediately,
    // without waiting for the next 250 ms poll tick. Prevents the
    // NAV_OK handler from seeing a stale active session after the
    // controls auto-hide and polling stops.
    dispatch(refreshTrickPlayState());
  };

/**
 * Handle a single FF / RW remote-key press (Workflow C).
 * Cycles the active speed level; in the opposite direction the
 * manager reduces speed by one level (TP-FF-9).
 */
export const speedCycle =
  (direction: Direction) =>
  (dispatch: UnityAppDispatch): void => {
    const trickPlayManager: TrickPlayManager | null =
      VideoPlaybackController.getInstance().getTrickPlayManager();
    const isDirectionRestricted: boolean =
      trickPlayManager !== null &&
      ((direction === Direction.Forward &&
        !trickPlayManager.getEligibility().allowForward) ||
        (direction === Direction.Backward &&
          !trickPlayManager.getEligibility().allowBackward));

    if (isDirectionRestricted) {
      dispatch(showToastMessage(getRestrictionToastMessage(direction)));
      return;
    }

    dispatch(speedTriggerMethodUpdated(false));
    // Cycle FIRST, then notify the thumbnail manager: onSeekStarted needs the
    // POST-cycle level to size its prefetch, and the target position does not
    // move on a speed change (the impl only restarts its tick), so reordering
    // is behaviour-neutral for the other arguments.
    VideoPlaybackController.getInstance().trickPlaySpeedCycle(direction);
    // With no trick-play manager the cycle above was a no-op, so there is
    // nothing to tell the thumbnail manager either.
    if (DeviceModel.isTvDevice() && trickPlayManager !== null) {
      const target: number =
        VideoPlaybackController.getInstance().getTrickPlayTargetPosition();
      // Both the direction AND the level must come from the manager, never
      // from the pressed button. A counter-direction press does NOT reverse
      // the seek: it reduces the speed by one level and keeps seeking the
      // original way (TP-FF-9, TrickPlayManager.speedCycle). Passing the
      // button direction told the manager the user had reversed, so it
      // cancelled and aborted the look-ahead the user was actually seeking
      // into and extended the opposite way for the rest of the session.
      const seekDirection: Direction | null =
        trickPlayManager.getCurrentDirection();
      if (seekDirection === null) {
        // A counter-direction press at L1 exits the session through
        // TrickPlayManager.confirm() internally, so there is no seek left to
        // prefetch for — tear the directional session down instead of leaving
        // it extending against a scrub that is over.
        thumbnailManager.onSeekConfirmed(target);
      } else {
        thumbnailManager.onSeekStarted(
          seekDirection,
          target,
          trickPlayManager.getCurrentLevel()
        );
      }
    }
    dispatch(refreshTrickPlayState());
  };

// ── Audio track thunks ──

/**
 * Reads the available audio tracks (and the active selection) from the
 * VideoPlaybackController and syncs them into Redux for the audio-track
 * selection panel.
 */
export const refreshAudioTracks =
  () =>
  (dispatch: UnityAppDispatch): void => {
    try {
      const vpc: VideoPlaybackController =
        VideoPlaybackController.getInstance();
      const audioTracks = vpc.getAudioTrackOptions();
      const selectedTrack = vpc.getSelectedAudioTrack();
      dispatch(
        audioTracksUpdated({
          audioTracks: audioTracks,
          selectedAudioTrackId: selectedTrack !== null ? selectedTrack.id : null
        })
      );
    } catch (error: unknown) {
      Log.e(TAG, "refreshAudioTracks()", error);
    }
  };

/**
 * Selects the audio track with the given id, then re-syncs the track
 * list and active selection into Redux.
 *
 * @param audioId - Identifier of the audio track to activate.
 */
export const setAudioTrack =
  (audioId: number) =>
  (dispatch: UnityAppDispatch): void => {
    try {
      VideoPlaybackController.getInstance().setAudioTrack(audioId);
      dispatch(refreshAudioTracks());
    } catch (error: unknown) {
      Log.e(TAG, "setAudioTrack()", error);
    }
  };

/**
 * Triggers single explicit recording for the currently playing content.
 */
export const requestSingleRecording =
  () =>
  async (
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ): Promise<boolean> => {
    try {
      if (!selectIsNpvrRecordingAllowed(getState())) {
        Log.w(
          TAG,
          "requestSingleRecording(): blocked by npvrAllowRecording=false in Redux state"
        );
        return false;
      }

      const playable: IPlayable | null =
        VideoPlaybackController.getInstance().getPlayable();
      if (playable === null || playable.channel === undefined) {
        Log.w(
          TAG,
          "requestSingleRecording(): no playable or channel available"
        );
        return false;
      }

      // The button should already be disabled for TiVo+; this refuses the
      // request if it is reached another way, so no recording is ever
      // scheduled against a channel that cannot be recorded.
      if (ChannelsInteractor.isTivoPlusChannel(playable.channel)) {
        Log.w(
          TAG,
          "requestSingleRecording(): blocked — TiVo+ channels are not recordable"
        );
        return false;
      }

      // Id resolution lives here rather than in the interactor: this is the
      // only caller with a playback session, so it is the only one whose
      // metadata describes the programme being recorded. Falling back to it
      // from a detail screen would record whatever happens to be playing.
      const vpc = VideoPlaybackController.getInstance();
      const offerId: string = playable.offerId || vpc.getCurrentOfferId() || "";
      const contentId: string | undefined =
        playable.contentId || vpc.getCurrentContentId() || undefined;
      const airingStartTime: string | undefined =
        vpc.getAiringStartTime() || undefined;

      // Fires `createRecording` on success — see
      // RecordingsServiceInteractor.createOfferRecording().
      await new RecordingsServiceInteractor().createOfferRecording(
        {
          offerId,
          contentId,
          channelNumber: playable.channel.channelNumber,
          channelId: playable.channel.channelId,
          airingStartTime
        },
        undefined,
        { screen: "Player" }
      );
      dispatch(
        showToastMessage(
          Locale.getString(
            LocaleKeys.PLAYER_RECORDING_SCHEDULED_THIS_PROGRAM_TOAST
          )
        )
      );

      // The OSD is still showing the pre-schedule action list, which offers
      // Record, so re-prepare it here; ACTION_LIST_READY then refreshes the
      // kernel types in Redux via subscribeMetadataStateListener.
      //
      // The metadata models fetch actions with isForced === false, so this
      // read goes through the domain-lib action cache — but
      // `createOfferRecording` has just dropped that cache (see
      // `clearServiceCache`), so it misses and refetches, and the pill flips
      // to Modify recording rather than replaying the pre-schedule list.
      vpc.prepareMetadataByType(playable, MetadataType.ACTION_LIST);
      return true;
    } catch (error: unknown) {
      // Scheduling failed — surface it instead of failing silently.
      Log.e(TAG, "requestSingleRecording()", error);
      dispatch(
        showToastMessage(
          recordingActionErrorMapper.appendErrorCode(
            Locale.getString(LocaleKeys.REQUEST_FAILED_TOAST),
            error
          )
        )
      );
      return false;
    }
  };

/**
 * Opens the series recording options overlay for the currently playing content.
 *
 * @param onePassInfo - OnePass action info containing ucid and series title.
 */
export const requestSeriesRecording =
  (onePassInfo: TOnePassActionInfo) =>
  (dispatch: UnityAppDispatch): void => {
    try {
      if (onePassInfo.ucid === "") {
        Log.w(TAG, "requestSeriesRecording(): ucid is empty");
        return;
      }
      /**
       * Screen identity for uiError attribution — see {@link OperationalLogger.logUiError}.
       * Only used by {@link requestSeriesRecording} below, so it's declared here
       * rather than with the file's other module-scope constants.
       *
       * TODO: this only covers recording from Live TV playback today; the player
       * also hosts other playback contexts (VOD, etc.) that don't yet go through
       * this code path.
       * https://github.com/tivocorp/unity-client-doc/pull/202
       */
      const PLAYER_LIVE_TV_SCREEN_ID: string = "player_live_tv";
      const PLAYER_LIVE_TV_SCREEN_NAME: string = "Live TV Player";
      dispatch(
        loadSeriesRecordingOptions({
          ucid: onePassInfo.ucid,
          title: onePassInfo.title,
          mode: "create",
          screen: PLAYER_LIVE_TV_SCREEN_NAME,
          screenId: PLAYER_LIVE_TV_SCREEN_ID
        })
      );
    } catch (error: unknown) {
      Log.e(TAG, "requestSeriesRecording()", error);
    }
  };

/**
 * Returns true when the recording quota is insufficient to cover the remaining
 * airing time.
 */
function isStorageLimitExceeded(
  freeMinutes: number,
  airingEndEpoch: number
): boolean {
  const nowEpochSeconds: number = Math.floor(Date.now() / 1000);
  const pendingSeconds: number = airingEndEpoch - nowEpochSeconds;
  const pendingMinutes: number =
    pendingSeconds > 0 ? Math.ceil(pendingSeconds / 60) : 0;
  return freeMinutes < pendingMinutes;
}

/**
 * Surfaces the storage-limit overlay when the user does not have enough quota
 * to record the remaining airing time.
 */
function showStorageLimitOverlay(
  dispatch: UnityAppDispatch,
  navigateTo: (url: string) => void,
  quota: TMyHubRecordingQuota,
  onContinue: () => void
): void {
  const message: string = Locale.getString(
    LocaleKeys.PLAYER_RECORDING_STORAGE_OVERLAY_MESSAGE
  ).replace("{{freeMinutes}}", String(quota.freeMinutes));

  dispatch(
    showUnityDialog({
      title: Locale.getString(
        LocaleKeys.PLAYER_RECORDING_STORAGE_OVERLAY_TITLE
      ),
      message: message,
      variant: "info",
      actions: [
        {
          id: "storage-continue",
          label: Locale.getString(LocaleKeys.PLAYER_RECORDING_CONTINUE_BUTTON),
          onPress: onContinue
        },
        {
          id: "storage-cancel",
          label: Locale.getString(LocaleKeys.ACTION_CANCEL),
          onPress: (): void => {
            dispatch(dismissUnityDialog());
          }
        },
        {
          id: "storage-manage-recording",
          label: Locale.getString(LocaleKeys.PLAYER_RECORDING_MANAGE_BUTTON),
          onPress: (): void => {
            navigateTo(LocationUrls.MY_HUB);
          }
        }
      ]
    })
  );
}

/**
 * Shows the recording choice dialog so the user can pick between recording all
 * episodes (OnePass) or this episode only.
 */
function showRecordingOverlay(
  dispatch: UnityAppDispatch,
  onePassInfo: TOnePassActionInfo
): void {
  const overlayMessage: string = Locale.getString(
    LocaleKeys.PLAYER_RECORDING_OVERLAY_MESSAGE
  ).replace("{{seriesTitle}}", onePassInfo.title);

  dispatch(
    showUnityDialog({
      title: Locale.getString(LocaleKeys.PLAYER_RECORDING_SET_TITLE),
      message: overlayMessage,
      variant: "info",
      actions: [
        {
          id: "record-all-episodes",
          label: Locale.getString(LocaleKeys.PLAYER_RECORD_ALL_EPISODES),
          onPress: (): void => {
            dispatch(requestSeriesRecording(onePassInfo));
          }
        },
        {
          id: "record-this-episode-only",
          label: Locale.getString(LocaleKeys.PLAYER_RECORD_THIS_EPISODE_ONLY),
          onPress: (): void => {
            dispatch(requestSingleRecording()).catch((error: unknown): void => {
              Log.e(TAG, "record-this-episode-only onPress", error);
            });
          }
        }
      ]
    })
  );
}

/** The navigation the recording flow needs — `useNavigationService().navigateTo`. */
type TRecordingNavigateTo = (
  url: string,
  replace?: boolean,
  state?: Record<string, unknown>
) => void;

/**
 * Shows the recording choice dialog for a series that already has a OnePass:
 * record this episode only, or manage the existing series recording.
 */
function showManageOnePassOverlay(
  dispatch: UnityAppDispatch,
  navigateTo: TRecordingNavigateTo,
  onePassModifyInfo: TOnePassActionInfo | null
): void {
  const overlayMessage: string = Locale.getString(
    LocaleKeys.PLAYER_RECORDING_OVERLAY_MESSAGE
  ).replace(
    "{{seriesTitle}}",
    onePassModifyInfo === null ? "" : onePassModifyInfo.title
  );

  dispatch(
    showUnityDialog({
      title: Locale.getString(LocaleKeys.PLAYER_RECORDING_SET_TITLE),
      message: overlayMessage,
      variant: "info",
      actions: [
        {
          id: "record-this-episode-only",
          label: Locale.getString(LocaleKeys.PLAYER_RECORD_THIS_EPISODE_ONLY),
          onPress: (): void => {
            dispatch(requestSingleRecording()).catch((error: unknown): void => {
              Log.e(TAG, "record-this-episode-only onPress", error);
            });
          }
        },
        {
          id: "manage-recording",
          label: Locale.getString(LocaleKeys.PLAYER_RECORDING_MANAGE_BUTTON),
          onPress: (): void => {
            // Same fallback as the Manage Recording button: without a
            // collection there is no series to open, so go to My Hub.
            if (onePassModifyInfo === null) {
              navigateTo(LocationUrls.MY_HUB);
              return;
            }
            navigateTo(LocationUrls.MY_HUB_MANAGE_SCREEN, false, {
              unifiedCollectionId: onePassModifyInfo.ucid
            });
          }
        }
      ]
    })
  );
}

/**
 * Top-level recording handler: checks quota, then shows the recording choice
 * overlay that fits the series' OnePass state, or goes straight to a single
 * recording:
 *   - a OnePass create action: record this episode only / record all episodes;
 *   - a OnePass modify action (the series already has a OnePass): record this
 *     episode only / manage recording;
 *   - neither: a single recording, no overlay.
 */
export const initiateRecording =
  (navigateTo: TRecordingNavigateTo) =>
  async (
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ): Promise<void> => {
    try {
      const quota = await dispatch(fetchMyHubRecordingQuota()).unwrap();
      const airingEndEpoch: number = selectAiringEndEpoch(getState());

      const proceed = (): void => {
        if (selectHasOnePassCreateActionKernel(getState())) {
          const onePassInfo: TOnePassActionInfo = selectOnePassActionInfo(
            getState()
          ) as TOnePassActionInfo;
          showRecordingOverlay(dispatch, onePassInfo);
        } else if (selectHasOnePassModifyActionKernel(getState())) {
          showManageOnePassOverlay(
            dispatch,
            navigateTo,
            selectOnePassModifyInfo(getState())
          );
        } else {
          dispatch(requestSingleRecording()).catch((error: unknown): void => {
            Log.e(TAG, "initiateRecording proceed", error);
          });
        }
      };

      if (isStorageLimitExceeded(quota.freeMinutes, airingEndEpoch)) {
        showStorageLimitOverlay(dispatch, navigateTo, quota, proceed);
        return;
      }

      proceed();
      return;
    } catch (error: unknown) {
      Log.e(TAG, "initiateRecording()", error);
    }
  };

// ── Standalone Playback ───────────────────────────────────────────────────────
//
// Standalone media is a progressive file (an EAS alert tone and similar) played
// by a dedicated glue player, separately from main playback. These thunks are
// the ViewModel side of that path: they mirror the model's end-of-media signal
// into Redux so the View can act on it, and give the View a way to drive
// playback without reaching into the model directly.

/**
 * Subscribes to the VPC standalone playback listener and mirrors each event
 * into Redux.
 *
 * Unlike the other player listeners, this one belongs somewhere always
 * mounted rather than on the player screen: an emergency alert is not allowed
 * to be suppressed by whatever the user happens to be watching, so the
 * subscription must outlive any single route.
 */
export const subscribeStandalonePlaybackListener =
  () =>
  (dispatch: UnityAppDispatch): void => {
    // Each call builds a fresh handler closure, and SubscriptionModel only
    // dedupes by reference — so this guard is what prevents a double-subscribe.
    cleanupStandalonePlaybackListener();

    const vpc: VideoPlaybackController = VideoPlaybackController.getInstance();
    const listener: ISubscription<TStandalonePlaybackEvent> =
      vpc.getStandalonePlaybackEventListener();

    const handleStandalonePlaybackEvent = (
      event: TStandalonePlaybackEvent
    ): void => {
      Log.i(
        TAG,
        "handleStandalonePlaybackEvent(): state=" + String(event.state)
      );
      dispatch(
        standalonePlaybackStateUpdated({
          state: event.state,
          errorMessage: event.errorMessage
        })
      );
    };

    listener.subscribe(handleStandalonePlaybackEvent);
    standalonePlaybackUnsubscribe = (): void => {
      listener.unsubscribe(handleStandalonePlaybackEvent);
    };
  };

/**
 * Synchronous cleanup — unsubscribes the standalone playback listener.
 */
export function cleanupStandalonePlaybackListener(): void {
  if (standalonePlaybackUnsubscribe !== null) {
    standalonePlaybackUnsubscribe();
    standalonePlaybackUnsubscribe = null;
  }
}

/**
 * Unsubscribes the standalone playback listener.
 */
export const unsubscribeStandalonePlaybackListener =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    cleanupStandalonePlaybackListener();
  };

/**
 * Interrupt main playback and start standalone media playback.
 *
 * @param url - The media URL to play.
 */
export const startStandalonePlayback =
  (url: string) =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance()
      .startStandalonePlayback(url)
      .catch((error: unknown): void => {
        Log.e(TAG, "startStandalonePlayback()", error);
      });
  };

/**
 * Stop standalone media playback. Main playback is left as it is.
 */
export const stopStandalonePlayback =
  () =>
  (_dispatch: UnityAppDispatch): void => {
    VideoPlaybackController.getInstance().stopStandalonePlayback();
  };
